import { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate, useParams, useSearchParams, Navigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Sprout, LayoutDashboard, Users, Briefcase, Calendar, Settings,
  LogOut, Plus, Trash2, CheckCircle2, XCircle, Clock, MessageSquare,
  ChevronRight, User, Phone, Mail, MapPin, ClipboardList, Euro,
  Layers, Edit, Copy, ExternalLink, CalendarClock, TrendingUp, 
  CreditCard, Eye, EyeOff, Search, Filter, UserPlus, DollarSign, ArrowUpRight,
  ArrowDownRight, Activity, FileText, Upload, Download, Image, Star,
  Menu, X, AlertTriangle, AlertCircle, Check, Loader2, Send, Info, Code, Globe, Key, Camera, ChevronDown, Video, ShoppingCart, FlaskConical
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { companyApi } from '../../lib/api';
import ErrorBoundary from '../../components/ErrorBoundary';
import HealthAlertBanner from '../../components/HealthAlertBanner';
import SmtpWizard from '../../components/SmtpWizard';
import VersionBadge from '../../components/VersionBadge';
import { useAuth } from '../../context/AuthContext';
import { FooterMinimal } from '../../components/Footer';
import AdvancedPlanningPage from './AdvancedPlanning';
import UnifiedPlanningPage from './UnifiedPlanning';
import WidgetEditor from './WidgetEditor';
import QuickActionFab from '../../components/QuickActionFab';
import useFabAction from '../../hooks/useFabAction';
import GuideTour, { WidgetsGuideTour, ClientsGuideTour } from '../../components/GuideTour';
import { getAssetUrl } from '../../lib/assetUrl';
import { DashboardOverview, ServicesPage, CollaboratorsPage, PunctualBookingsPage, ReviewsPage, RequestsPage, AppointmentRequestsPage, QuoteRequestsPage, CommercialAgendaPage, WorkHoursPage, PaymentsPage, UnifiedRequestsPage, ClientDetailPageNew, SubscriptionDetailPage, ClosuresPage, LeaveHistoryPage, LeaveRequestsPage, SlotTypesPage, RdvPlanningPage, TrashPage } from './pages';
import NotificationBell from '../../components/NotificationBell';
import AnnouncementBanner from '../../components/AnnouncementBanner';
import PushNotificationToggle from '../../components/PushNotificationToggle';
import NewVersionToast from '../../components/NewVersionToast';
import NotificationsPage from '../NotificationsPage';

// DashboardOverview moved to ./pages/DashboardOverview.js

// CollaboratorsPage moved to ./pages/CollaboratorsPage.js

// Client Detail Page - Full page view
function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isExchangeOpen, setIsExchangeOpen] = useState(false);
  const [newExchange, setNewExchange] = useState({
    exchange_type: 'call', direction: 'outgoing', subject: '', content: '', duration_minutes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientRes, collabRes, exchangesRes] = await Promise.all([
          companyApi.getClient(clientId),
          companyApi.getCollaborators(),
          companyApi.getClientExchanges(clientId)
        ]);
        setClient(clientRes.data);
        setCollaborators(collabRes.data);
        setExchanges(exchangesRes.data || []);
      } catch (error) {
        toast.error('Client non trouvé');
        navigate('/company/clients');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [clientId, navigate]);

  const getCollaboratorName = (id) => {
    const collab = collaborators.find(c => c.id === id);
    return collab ? collab.name : 'Non assigné';
  };

  const handleAddExchange = async () => {
    if (!newExchange.content && !newExchange.subject) {
      toast.error('Veuillez ajouter un sujet ou un contenu');
      return;
    }
    setIsSubmitting(true);
    try {
      await companyApi.createClientExchange(clientId, {
        ...newExchange,
        duration_minutes: newExchange.duration_minutes ? parseInt(newExchange.duration_minutes) : null
      });
      toast.success('Échange enregistré');
      setIsExchangeOpen(false);
      setNewExchange({ exchange_type: 'call', direction: 'outgoing', subject: '', content: '', duration_minutes: '' });
      // Refresh exchanges
      const res = await companyApi.getClientExchanges(clientId);
      setExchanges(res.data || []);
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteExchange = async (exchangeId) => {
    if (!window.confirm('Supprimer cet échange ?')) return;
    try {
      await companyApi.deleteClientExchange(clientId, exchangeId);
      toast.success('Échange supprimé');
      const res = await companyApi.getClientExchanges(clientId);
      setExchanges(res.data || []);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getExchangeIcon = (type) => {
    switch(type) {
      case 'call': return <Phone className="w-4 h-4" />;
      case 'sms': return <MessageSquare className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'meeting': return <Users className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const calculateTotalCA = () => {
    if (!client?.bookings) return 0;
    return client.bookings.reduce((sum, b) => sum + (b.remaining_charge || b.invoice_amount || 0), 0);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted rounded w-1/3 animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/company/clients')}>
          <ChevronRight className="w-5 h-5 rotate-180" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-heading font-semibold">{client.name}</h1>
          <p className="text-muted-foreground">Fiche client</p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/company/clients/${clientId}/edit`)}>
          <Edit className="w-4 h-4 mr-2" /> Modifier
        </Button>
      </div>

      {/* Client Info Header */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contact Info */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Informations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-lg">{client.name}</p>
                <p className="text-sm text-muted-foreground">
                  Client depuis {client.created_at ? new Date(client.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : '-'}
                </p>
              </div>
            </div>
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <a href={`mailto:${client.email}`} className="text-sm hover:text-primary">{client.email}</a>
              </div>
              {client.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <a href={`tel:${client.phone}`} className="text-sm hover:text-primary">{client.phone}</a>
                </div>
              )}
              {client.address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <span className="text-sm">{client.address}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Statistiques</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-green-50 rounded-xl">
                <p className="text-xs text-green-600 font-medium">Chiffre d'affaires</p>
                <p className="text-2xl font-bold text-green-700">{calculateTotalCA().toFixed(0)}€</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl">
                <p className="text-xs text-blue-600 font-medium">Interventions</p>
                <p className="text-2xl font-bold text-blue-700">{client.bookings?.length || 0}</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-xl">
                <p className="text-xs text-purple-600 font-medium">Crédits restants</p>
                <p className="text-2xl font-bold text-purple-700">{client.subscription?.remaining_credits || 0}h</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-xl">
                <p className="text-xs text-amber-600 font-medium">Échanges</p>
                <p className="text-2xl font-bold text-amber-700">{exchanges.length}</p>
              </div>
            </div>

            {/* Subscription Info */}
            {client.subscription && (
              <div className="mt-4 p-4 border rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-medium flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> Abonnement
                  </p>
                  <Badge className={client.subscription.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                    {client.subscription.is_active ? 'Actif' : 'Inactif'}
                  </Badge>
                </div>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Fréquence</p>
                    <p className="font-medium capitalize">
                      {client.subscription.frequency === 'monthly' ? 'Mensuel' :
                       client.subscription.frequency === 'biweekly' ? 'Bi-mensuel' :
                       client.subscription.frequency === 'weekly' ? 'Hebdomadaire' : client.subscription.frequency}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Type</p>
                    <p className="font-medium">{client.subscription.slot_type === 'half_day' ? 'Demi-journée' : 'Journée'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tarif</p>
                    <p className="font-medium">{client.subscription.price_per_hour || 35}€/h</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Passages/an</p>
                    <p className="font-medium">{client.subscription.passages_per_year || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Historique des prestations</TabsTrigger>
          <TabsTrigger value="exchanges">Suivi des échanges ({exchanges.length})</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>

        {/* Prestations Tab */}
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Historique des prestations</CardTitle>
            </CardHeader>
            <CardContent>
              {client.bookings && client.bookings.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Prestation</TableHead>
                      <TableHead>Intervenant</TableHead>
                      <TableHead>Durée</TableHead>
                      <TableHead>Montant</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {client.bookings.sort((a, b) => new Date(b.date) - new Date(a.date)).map((booking, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{new Date(booking.date).toLocaleDateString('fr-FR')}</p>
                            <p className="text-xs text-muted-foreground">{booking.slot_type === 'half_day' ? 'Demi-journée' : 'Journée'}</p>
                          </div>
                        </TableCell>
                        <TableCell>{booking.reason || booking.service_name || 'Entretien'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                              <User className="w-3 h-3 text-primary" />
                            </div>
                            <span className="text-sm">{getCollaboratorName(booking.collaborator_id)}</span>
                          </div>
                        </TableCell>
                        <TableCell>{booking.hours_used || (booking.slot_type === 'half_day' ? 4 : 7)}h</TableCell>
                        <TableCell className="font-medium">
                          {(booking.remaining_charge || booking.invoice_amount || 0).toFixed(0)}€
                        </TableCell>
                        <TableCell>
                          <Badge variant={booking.status === 'completed' ? 'default' : booking.status === 'confirmed' ? 'secondary' : 'outline'}>
                            {booking.status === 'completed' ? 'Terminé' : 
                             booking.status === 'confirmed' ? 'Confirmé' : 
                             booking.status === 'cancelled' ? 'Annulé' : booking.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune prestation enregistrée</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exchanges Tab */}
        <TabsContent value="exchanges" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Suivi des échanges</CardTitle>
                <Button onClick={() => setIsExchangeOpen(true)} size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter un échange
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {exchanges.length > 0 ? (
                <div className="space-y-4">
                  {exchanges.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((exchange) => (
                    <div key={exchange.id} className="flex gap-4 p-4 border rounded-lg">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        exchange.exchange_type === 'call' ? 'bg-blue-100 text-blue-600' :
                        exchange.exchange_type === 'email' ? 'bg-purple-100 text-purple-600' :
                        exchange.exchange_type === 'sms' ? 'bg-green-100 text-green-600' :
                        exchange.exchange_type === 'meeting' ? 'bg-amber-100 text-amber-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {getExchangeIcon(exchange.exchange_type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{exchange.subject || exchange.exchange_type}</p>
                            <Badge variant="outline" className="text-xs">
                              {exchange.direction === 'incoming' ? 'Entrant' : 'Sortant'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              {new Date(exchange.created_at).toLocaleDateString('fr-FR')} à {new Date(exchange.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteExchange(exchange.id)}>
                              <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                            </Button>
                          </div>
                        </div>
                        {exchange.content && (
                          <p className="text-sm text-muted-foreground mt-1">{exchange.content}</p>
                        )}
                        {exchange.duration_minutes && (
                          <p className="text-xs text-muted-foreground mt-1">Durée: {exchange.duration_minutes} min</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun échange enregistré</p>
                  <Button variant="outline" className="mt-4" onClick={() => setIsExchangeOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Ajouter le premier échange
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              {client.notes ? (
                <p className="whitespace-pre-wrap">{client.notes}</p>
              ) : (
                <p className="text-muted-foreground text-center py-8">Aucune note pour ce client</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Exchange Dialog */}
      <Dialog open={isExchangeOpen} onOpenChange={setIsExchangeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un échange</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type d'échange</Label>
                <Select value={newExchange.exchange_type} onValueChange={(v) => setNewExchange({ ...newExchange, exchange_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Appel</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="meeting">Réunion</SelectItem>
                    <SelectItem value="note">Note</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Direction</Label>
                <Select value={newExchange.direction} onValueChange={(v) => setNewExchange({ ...newExchange, direction: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outgoing">Sortant</SelectItem>
                    <SelectItem value="incoming">Entrant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sujet</Label>
              <Input
                value={newExchange.subject}
                onChange={(e) => setNewExchange({ ...newExchange, subject: e.target.value })}
                placeholder="Ex: Confirmation de RDV"
              />
            </div>
            <div className="space-y-2">
              <Label>Contenu / Notes</Label>
              <Textarea
                value={newExchange.content}
                onChange={(e) => setNewExchange({ ...newExchange, content: e.target.value })}
                placeholder="Détails de l'échange..."
                rows={3}
              />
            </div>
            {(newExchange.exchange_type === 'call' || newExchange.exchange_type === 'meeting') && (
              <div className="space-y-2">
                <Label>Durée (minutes)</Label>
                <Input
                  type="number"
                  value={newExchange.duration_minutes}
                  onChange={(e) => setNewExchange({ ...newExchange, duration_minutes: e.target.value })}
                  placeholder="Ex: 15"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsExchangeOpen(false)}>Annuler</Button>
            <Button onClick={handleAddExchange} disabled={isSubmitting}>
              {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// CRM Clients Page
function ClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  // Allow deep-link from the dashboard banner with ?filter=pending
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const f = searchParams.get('filter');
    if (f && ['all', 'active', 'inactive', 'pending'].includes(f)) {
      setFilterStatus(f);
    }
  }, [searchParams]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', address: '', notes: '' });
  const [bookingForm, setBookingForm] = useState({ date: '', slot_type: 'half_day', reason: '', collaborator_id: '' });
  const [invoiceForm, setInvoiceForm] = useState({ file: null, invoice_amount: '' });
  const [createForm, setCreateForm] = useState({
    name: '', email: '', phone: '', address: '', password: '',
    create_subscription: false, subscription_frequency: 'monthly',
    slot_type: 'half_day', price_per_hour: 35, passages_per_year: 12,
    send_credentials_email: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Available slots for booking
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [selectedWeekStart, setSelectedWeekStart] = useState(null);
  
  // States for exchanges/interactions
  const [exchanges, setExchanges] = useState([]);
  const [isExchangeOpen, setIsExchangeOpen] = useState(false);
  const [newExchange, setNewExchange] = useState({
    exchange_type: 'call', direction: 'outgoing', subject: '', content: '', duration_minutes: ''
  });
  
  // CSV Import/Export states
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const fetchClients = async () => {
    try {
      const [clientsRes, collabRes] = await Promise.all([
        companyApi.getClients(),
        companyApi.getCollaborators()
      ]);
      setClients(clientsRes.data);
      setCollaborators(collabRes.data);
    } catch (error) {
      console.error('fetchClients error:', error);
      const detail = error?.response?.data?.detail || error?.message || 'inconnue';
      toast.error(`Erreur lors du chargement (${detail})`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  // Fetch exchanges when client detail is opened
  const fetchExchanges = async (clientId) => {
    try {
      const response = await companyApi.getClientExchanges(clientId);
      setExchanges(response.data);
    } catch (error) {
      console.error('Error fetching exchanges:', error);
      setExchanges([]);
    }
  };

  const handleAddExchange = async () => {
    if (!newExchange.content && !newExchange.subject) {
      toast.error('Veuillez ajouter un sujet ou un contenu');
      return;
    }
    try {
      await companyApi.createClientExchange(selectedClient.id, {
        ...newExchange,
        duration_minutes: newExchange.duration_minutes ? parseInt(newExchange.duration_minutes) : null
      });
      toast.success('Échange enregistré');
      setIsExchangeOpen(false);
      setNewExchange({ exchange_type: 'call', direction: 'outgoing', subject: '', content: '', duration_minutes: '' });
      fetchExchanges(selectedClient.id);
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteExchange = async (exchangeId) => {
    if (!window.confirm('Supprimer cet échange ?')) return;
    try {
      await companyApi.deleteClientExchange(selectedClient.id, exchangeId);
      toast.success('Échange supprimé');
      fetchExchanges(selectedClient.id);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getExchangeIcon = (type) => {
    switch (type) {
      case 'call': return <Phone className="w-4 h-4" />;
      case 'sms': return <MessageSquare className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'meeting': return <Users className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getExchangeLabel = (type) => {
    switch (type) {
      case 'call': return 'Appel';
      case 'sms': return 'SMS';
      case 'email': return 'Email';
      case 'meeting': return 'Réunion';
      default: return 'Note';
    }
  };

  // Generate random password
  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let pwd = '';
    for (let i = 0; i < 10; i++) pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    setCreateForm(f => ({ ...f, password: pwd }));
  };

  // Open create dialog when redirected from FAB (`?action=new`)
  useFabAction({ new: () => { generatePassword(); setIsCreateOpen(true); } });

  // CSV Export
  const handleExportCSV = async () => {
    try {
      const response = await companyApi.exportClientsCSV();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clients_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  // CSV Template Download
  const handleDownloadTemplate = async () => {
    try {
      const response = await companyApi.getCSVTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_import_clients.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // CSV Import
  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error('Veuillez sélectionner un fichier CSV');
      return;
    }
    setIsImporting(true);
    setImportResult(null);
    try {
      const response = await companyApi.importClientsCSV(importFile);
      setImportResult(response.data);
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} client(s) importé(s)`);
        fetchClients();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setIsImporting(false);
    }
  };

  const handleCreateClient = async () => {
    if (!createForm.name?.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    // When the operator asked for an actual client-space login we still need
    // email + password. Otherwise it's a contact-only entry.
    if (createForm.create_account_access && (!createForm.email || !createForm.password)) {
      toast.error('Email et mot de passe requis pour créer un accès espace client');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        ...createForm,
        email: createForm.email?.trim() || null,
        phone: createForm.phone?.trim() || null,
        password: createForm.password?.trim() || null,
        address: createForm.address?.trim() || null,
      };
      const response = await companyApi.createClient(payload);
      const newClientId = response.data?.id;

      // Send credentials email only when an actual account has been created
      if (createForm.create_account_access && createForm.send_credentials_email && newClientId) {
        try {
          await companyApi.sendClientCredentials(newClientId);
          toast.success('Client créé et identifiants envoyés par email !');
        } catch (emailError) {
          toast.success(`Client créé ! Erreur lors de l'envoi de l'email. Mot de passe: ${createForm.password}`);
        }
      } else if (createForm.create_account_access) {
        toast.success(`Client créé ! Mot de passe temporaire: ${createForm.password}`);
      } else {
        toast.success('Client ajouté au carnet de contacts');
      }

      setIsCreateOpen(false);
      setCreateForm({
        name: '', email: '', phone: '', address: '', password: '',
        create_account_access: false,
        create_subscription: false, subscription_frequency: 'monthly',
        slot_type: 'half_day', price_per_hour: 35, passages_per_year: 12,
        send_credentials_email: true
      });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setIsSubmitting(false);
    }
  };


  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'active' && client.subscription?.is_active) ||
                         (filterStatus === 'inactive' && !client.subscription?.is_active) ||
                         (filterStatus === 'pending' && client.account_status === 'pending');
    return matchesSearch && matchesFilter;
  });

  // Count pending clients
  const pendingClientsCount = clients.filter(c => c.account_status === 'pending').length;

  // Handle approve/reject client
  const handleApproveClient = async (clientId) => {
    try {
      await companyApi.approveClient(clientId);
      toast.success('Compte client validé !');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const handleRejectClient = async (clientId) => {
    const reason = window.prompt('Motif du refus (optionnel) :');
    try {
      await companyApi.rejectClient(clientId, reason);
      toast.success('Compte client refusé');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du refus');
    }
  };

  const openDetail = async (client) => {
    try {
      const response = await companyApi.getClient(client.id);
      setSelectedClient(response.data);
      setIsDetailOpen(true);
      // Load exchanges when opening client detail
      fetchExchanges(client.id);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const openEdit = (client) => {
    setEditForm({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      notes: client.notes || ''
    });
    setSelectedClient(client);
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await companyApi.updateClient(selectedClient.id, editForm);
      toast.success('Client mis à jour');
      setIsEditOpen(false);
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Fetch available slots for booking
  const fetchAvailableSlots = async (weekStart = null) => {
    setIsLoadingSlots(true);
    try {
      // Get current week start if not provided
      const start = weekStart || new Date();
      start.setHours(0, 0, 0, 0);
      const dayOfWeek = start.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday
      start.setDate(start.getDate() + diff);
      setSelectedWeekStart(new Date(start));
      
      // Get next 2 weeks of dates
      const slots = [];
      for (let i = 0; i < 14; i++) {
        const date = new Date(start);
        date.setDate(date.getDate() + i);
        
        // Skip past dates
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date < today) continue;
        
        // Skip weekends
        if (date.getDay() === 0 || date.getDay() === 6) continue;
        
        slots.push({
          date: date.toISOString().split('T')[0],
          dayName: date.toLocaleDateString('fr-FR', { weekday: 'short' }),
          dayNum: date.getDate(),
          month: date.toLocaleDateString('fr-FR', { month: 'short' }),
          available_morning: true, // Default to available - would be enriched from backend
          available_afternoon: true,
        });
      }
      
      // TODO: Fetch actual availability from backend
      // const response = await companyApi.getAvailableSlots(start.toISOString().split('T')[0]);
      // Merge availability data
      
      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error fetching available slots:', error);
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const openBookingDialog = async () => {
    setBookingForm({ date: '', slot_type: 'half_day', reason: '', collaborator_id: '' });
    await fetchAvailableSlots();
    setIsBookingOpen(true);
  };

  const handleSlotSelect = (date, slotType) => {
    setBookingForm({ ...bookingForm, date, slot_type: slotType });
  };

  const navigateWeek = async (direction) => {
    const newStart = new Date(selectedWeekStart);
    newStart.setDate(newStart.getDate() + (direction * 7));
    await fetchAvailableSlots(newStart);
  };

  const handleCreateBooking = async () => {
    if (!bookingForm.date || !bookingForm.reason) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setIsSubmitting(true);
    try {
      await companyApi.createBookingForClient({
        client_id: selectedClient.id,
        date: bookingForm.date,
        slot_type: bookingForm.slot_type,
        reason: bookingForm.reason,
        collaborator_id: bookingForm.collaborator_id || null
      });
      toast.success('Réservation créée pour le client');
      setIsBookingOpen(false);
      setBookingForm({ date: '', slot_type: 'half_day', reason: '', collaborator_id: '' });
      // Refresh client details
      const response = await companyApi.getClient(selectedClient.id);
      setSelectedClient(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDepositInvoice = async () => {
    if (!invoiceForm.file || !invoiceForm.invoice_amount) {
      toast.error('Veuillez sélectionner un fichier PDF et entrer le montant');
      return;
    }
    setIsSubmitting(true);
    try {
      await companyApi.uploadInvoiceFile(
        selectedIntervention.id, 
        invoiceForm.file, 
        parseFloat(invoiceForm.invoice_amount)
      );
      toast.success('Facture déposée avec succès');
      setIsInvoiceOpen(false);
      setInvoiceForm({ file: null, invoice_amount: '' });
      // Refresh client details
      const response = await companyApi.getClient(selectedClient.id);
      setSelectedClient(response.data);
    } catch (error) {
      toast.error('Erreur lors du dépôt de la facture');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <ClientsGuideTour />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Clients</h1>
          <p className="text-muted-foreground mt-1">Gérez votre portefeuille clients</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={handleExportCSV}>
            <Download className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Exporter</span> CSV
          </Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setImportFile(null); setImportResult(null); setIsImportOpen(true); }}>
            <Upload className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Importer</span> CSV
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => navigate('/company/requests')}>
            <ClipboardList className="w-4 h-4 mr-2" />
            Demandes
          </Button>
          <Button className="rounded-full" data-testid="create-client-btn" onClick={() => { generatePassword(); setIsCreateOpen(true); }}>
            <UserPlus className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Créer un</span> client
          </Button>
        </div>
      </div>

      {/* CSV Import Dialog */}
      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Importer des clients</DialogTitle>
            <DialogDescription>
              Importez une liste de clients à partir d'un fichier CSV
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Format requis :</strong> Fichier CSV avec les colonnes : name, email, phone, address
              </p>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                <Download className="w-4 h-4 mr-2" />
                Télécharger le modèle
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Fichier CSV</Label>
              <Input 
                type="file" 
                accept=".csv" 
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              />
            </div>
            {importResult && (
              <div className={`p-4 rounded-lg ${importResult.errors?.length > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                <p className="font-medium mb-2">
                  {importResult.imported} client(s) importé(s)
                  {importResult.skipped > 0 && `, ${importResult.skipped} ignoré(s) (déjà existants)`}
                </p>
                {importResult.errors?.length > 0 && (
                  <div className="text-sm text-amber-700">
                    <p className="font-medium">Erreurs :</p>
                    <ul className="list-disc list-inside">
                      {importResult.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {importResult.errors.length > 5 && <li>... et {importResult.errors.length - 5} autres</li>}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsImportOpen(false)}>Fermer</Button>
            <Button onClick={handleImportCSV} disabled={!importFile || isImporting}>
              {isImporting ? 'Import en cours...' : 'Importer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Client Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Créer un nouveau client</DialogTitle>
            <DialogDescription>Ajoutez un client à votre portefeuille</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nom complet *</Label>
              <Input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="Jean Dupont" data-testid="create-client-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email {createForm.create_account_access && '*'}</Label>
                <Input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} placeholder="jean@example.com (optionnel)" data-testid="create-client-email-input" />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input value={createForm.phone} onChange={e => setCreateForm(f => ({ ...f, phone: e.target.value }))} placeholder="06 12 34 56 78" data-testid="create-client-phone-input" />
              </div>
            </div>
            <div>
              <Label>Adresse</Label>
              <Textarea value={createForm.address} onChange={e => setCreateForm(f => ({ ...f, address: e.target.value }))} placeholder="123 rue de la Paix, 75001 Paris" />
            </div>

            {/* Client space access toggle */}
            <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
              <div className="flex items-center gap-3">
                <Switch
                  checked={createForm.create_account_access}
                  onCheckedChange={v => setCreateForm(f => ({ ...f, create_account_access: v }))}
                  data-testid="create-account-access-switch"
                />
                <div>
                  <Label className="cursor-pointer">Créer un accès espace client</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Le client pourra se connecter à son espace personnel avec ses identifiants.
                  </p>
                </div>
              </div>
              {createForm.create_account_access && (
                <div className="pt-2 space-y-3 border-t">
                  <div>
                    <Label>Mot de passe *</Label>
                    <div className="flex gap-2">
                      <Input value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} data-testid="create-client-password-input" />
                      <Button type="button" variant="outline" size="sm" onClick={generatePassword}>Générer</Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={createForm.send_credentials_email}
                      onCheckedChange={v => setCreateForm(f => ({ ...f, send_credentials_email: v }))}
                    />
                    <Label className="cursor-pointer text-sm">Envoyer les identifiants par email</Label>
                  </div>
                </div>
              )}
            </div>
            
            <div className="border-t pt-4">
              <div className="flex items-center gap-3 mb-4">
                <Switch checked={createForm.create_subscription} onCheckedChange={v => setCreateForm(f => ({ ...f, create_subscription: v }))} />
                <Label className="cursor-pointer">Créer un abonnement</Label>
              </div>
              
              {createForm.create_subscription && (
                <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Fréquence</Label>
                      <Select value={createForm.subscription_frequency} onValueChange={v => setCreateForm(f => ({ ...f, subscription_frequency: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Hebdomadaire</SelectItem>
                          <SelectItem value="biweekly">Bi-mensuel</SelectItem>
                          <SelectItem value="monthly">Mensuel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Type de créneau</Label>
                      <Select value={createForm.slot_type} onValueChange={v => setCreateForm(f => ({ ...f, slot_type: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="half_day">Demi-journée (4h)</SelectItem>
                          <SelectItem value="full_day">Journée (7h)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tarif horaire (€/h)</Label>
                      <Input type="number" value={createForm.price_per_hour} onChange={e => setCreateForm(f => ({ ...f, price_per_hour: parseFloat(e.target.value) || 0 }))} />
                    </div>
                    <div>
                      <Label>Passages / an</Label>
                      <Input type="number" value={createForm.passages_per_year} onChange={e => setCreateForm(f => ({ ...f, passages_per_year: parseInt(e.target.value) || 0 }))} />
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground bg-primary/5 p-3 rounded">
                    <p>Estimation annuelle: {((createForm.slot_type === 'half_day' ? 4 : 7) * createForm.passages_per_year * createForm.price_per_hour).toFixed(2)}€</p>
                    <p>Mensualité client (après crédit): {((createForm.slot_type === 'half_day' ? 4 : 7) * createForm.passages_per_year * createForm.price_per_hour / 12 / 2).toFixed(2)}€/mois</p>
                  </div>
                </div>
              )}
            </div>

            {/* Option to send credentials by email */}
            <div className="border-t pt-4">
              <div className="flex items-center gap-3">
                <Switch 
                  checked={createForm.send_credentials_email} 
                  onCheckedChange={v => setCreateForm(f => ({ ...f, send_credentials_email: v }))} 
                />
                <div>
                  <Label className="cursor-pointer">Envoyer les identifiants par email</Label>
                  <p className="text-xs text-muted-foreground">Le client recevra ses identifiants de connexion et le lien vers son espace</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Annuler</Button>
              <Button onClick={handleCreateClient} disabled={isSubmitting} data-testid="confirm-create-client-btn">
                {isSubmitting ? 'Création...' : 'Créer le client'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les clients</SelectItem>
                <SelectItem value="pending">
                  En attente de validation {pendingClientsCount > 0 && `(${pendingClientsCount})`}
                </SelectItem>
                <SelectItem value="active">Abonnés actifs</SelectItem>
                <SelectItem value="inactive">Sans abonnement</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {pendingClientsCount > 0 && (
          <Card className="border-orange-200 bg-orange-50/50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('pending')}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-orange-700">{pendingClientsCount}</p>
                <p className="text-xs text-orange-600">En attente de validation</p>
              </div>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{clients.filter(c => c.account_status !== 'pending').length}</p>
              <p className="text-xs text-muted-foreground">Total clients</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{clients.filter(c => c.subscription?.is_active).length}</p>
              <p className="text-xs text-muted-foreground">Abonnés actifs</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{clients.filter(c => !c.subscription?.is_active && c.account_status !== 'pending').length}</p>
              <p className="text-xs text-muted-foreground">Sans abonnement</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{clients.reduce((sum, c) => sum + (c.booking_count || 0), 0)}</p>
              <p className="text-xs text-muted-foreground">Total réservations</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Clients List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center">Chargement...</div>
          ) : filteredClients.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Aucun client trouvé</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Abonnement</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow key={client.id} className={`cursor-pointer hover:bg-muted/50 ${client.account_status === 'pending' ? 'bg-orange-50/50' : ''}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${client.account_status === 'pending' ? 'bg-orange-100' : 'bg-primary/10'}`}>
                          <User className={`w-5 h-5 ${client.account_status === 'pending' ? 'text-orange-600' : 'text-primary'}`} />
                        </div>
                        <div className="min-w-0">
                          <button 
                            onClick={() => navigate(`/company/clients/${client.id}`)}
                            className="font-medium hover:text-primary hover:underline transition-colors text-left"
                          >
                            {client.name}
                          </button>
                          {client.is_test && (
                            <Badge className="ml-2 bg-orange-100 text-orange-700 hover:bg-orange-100 border-orange-300 text-[10px] gap-1" data-testid={`test-badge-${client.id}`}>
                              <FlaskConical className="w-3 h-3" />
                              TEST
                            </Badge>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {client.account_status === 'pending' ? 'Inscrit le ' + new Date(client.created_at).toLocaleDateString('fr-FR') : `${client.booking_count || 0} réservations`}
                          </p>
                          {/* Explicit account status row */}
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {client.space_activated ? (
                              <>
                                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-medium" data-testid={`space-activated-${client.id}`}>
                                  <Check className="w-3 h-3" />
                                  Mot de passe défini
                                </span>
                                <button
                                  type="button"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (!window.confirm(`Envoyer un email de réinitialisation du mot de passe à ${client.email} ?`)) return;
                                    try {
                                      await companyApi.sendClientPasswordReset(client.id);
                                      toast.success('Email de réinitialisation envoyé à ' + client.email);
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
                                    }
                                  }}
                                  className="text-[11px] text-slate-500 hover:text-slate-800 underline decoration-dotted underline-offset-2"
                                  data-testid={`reset-password-row-${client.id}`}
                                  title="Envoyer un lien de réinitialisation du mot de passe"
                                >
                                  Réinitialiser
                                </button>
                              </>
                            ) : (
                              <>
                                <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 font-medium" data-testid={`space-pending-${client.id}`}>
                                  <AlertCircle className="w-3 h-3" />
                                  Mot de passe non défini
                                </span>
                                <button
                                  type="button"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await companyApi.sendClientActivationLink(client.id);
                                      toast.success('Lien envoyé à ' + client.email);
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
                                    }
                                  }}
                                  className="text-[11px] text-amber-700 hover:text-amber-900 underline decoration-dotted underline-offset-2"
                                  data-testid={`resend-activation-row-${client.id}`}
                                >
                                  Renvoyer le lien
                                </button>
                              </>
                            )}
                          </div>
                          {/* Last login (if any) */}
                          {client.last_login_at && (
                            <p className="text-[10px] text-muted-foreground mt-0.5" data-testid={`last-login-${client.id}`}>
                              Dernière connexion : {new Date(client.last_login_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                          {!client.last_login_at && client.space_activated && (
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              Aucune connexion enregistrée
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{client.email}</p>
                      <p className="text-xs text-muted-foreground">{client.phone}</p>
                    </TableCell>
                    <TableCell>
                      {client.account_status === 'pending' ? (
                        <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                          En attente
                        </Badge>
                      ) : client.account_status === 'rejected' ? (
                        <Badge className="bg-red-100 text-red-700 border-red-200">
                          Refusé
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700 border-green-200">
                          Validé
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {client.account_status === 'pending' ? (
                        <span className="text-xs text-muted-foreground">-</span>
                      ) : client.subscription?.is_active ? (
                        <div>
                          <div className="flex items-center gap-1">
                            <span className="font-medium text-sm">
                              {client.scheduled_slots_this_month || 0}/{client.slots_per_month || 4}
                            </span>
                            <span className="text-xs text-muted-foreground">créneaux</span>
                          </div>
                          <p className="text-xs text-blue-600">
                            {client.subscription_frequency === 'weekly' ? 'Hebdo' :
                             client.subscription_frequency === 'biweekly' ? 'Bi-mensuel' :
                             client.subscription_frequency === 'monthly' ? 'Mensuel' :
                             client.subscription_frequency === 'custom' ? 'Personnalisé' :
                             'Abonné'}
                          </p>
                        </div>
                      ) : (
                        <Badge variant="secondary">Sans abo.</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {client.account_status === 'pending' ? (
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            size="sm" 
                            variant="default"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={(e) => { e.stopPropagation(); handleApproveClient(client.id); }}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Valider
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={(e) => { e.stopPropagation(); handleRejectClient(client.id); }}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Refuser
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openDetail(client)} title="Voir">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(client)} title="Modifier">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title={client.is_test ? 'Retirer le drapeau TEST' : 'Marquer comme TEST'}
                            data-testid={`toggle-test-${client.id}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await companyApi.setTestFlag('clients', client.id, !client.is_test);
                                toast.success(client.is_test ? 'Flag TEST retiré' : 'Marqué comme TEST');
                                fetchClients();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Erreur');
                              }
                            }}
                          >
                            <FlaskConical className={`w-4 h-4 ${client.is_test ? 'text-orange-500' : 'text-muted-foreground'}`} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Envoyer en corbeille"
                            data-testid={`trash-${client.id}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Envoyer "${client.name}" en corbeille ? Restaurable à tout moment.`)) return;
                              try {
                                await companyApi.softDeleteEntity('clients', client.id);
                                toast.success('Client envoyé en corbeille');
                                fetchClients();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Erreur');
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Client Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Fiche Client</DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-6">
              {/* Client Info Header */}
              <div className="flex flex-col md:flex-row items-start justify-between gap-4 p-4 bg-muted/30 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="w-10 h-10 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold">{selectedClient.name}</h3>
                    <div className="mt-2 space-y-1">
                      <p className="text-sm flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <a href={`mailto:${selectedClient.email}`} className="hover:text-primary">{selectedClient.email}</a>
                      </p>
                      {selectedClient.phone && (
                        <p className="text-sm flex items-center gap-2">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <a href={`tel:${selectedClient.phone}`} className="hover:text-primary">{selectedClient.phone}</a>
                        </p>
                      )}
                      {selectedClient.address && (
                        <p className="text-sm flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          {selectedClient.address}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    className="rounded-full" 
                    onClick={() => {
                      openEdit(selectedClient);
                      setIsDetailOpen(false);
                    }}
                    data-testid="edit-client-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Modifier
                  </Button>
                  <Button 
                    variant="outline" 
                    className="rounded-full" 
                    onClick={async () => {
                      try {
                        await companyApi.sendClientCredentials(selectedClient.id);
                        toast.success('Identifiants envoyés par email !');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
                      }
                    }}
                    data-testid="send-credentials-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer identifiants
                  </Button>
                  {!selectedClient.space_activated ? (
                    <Button 
                      variant="outline" 
                      className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50" 
                      onClick={async () => {
                        try {
                          await companyApi.sendClientActivationLink(selectedClient.id);
                          toast.success('Lien d\'activation envoyé !');
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
                        }
                      }}
                      data-testid="send-activation-link-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Envoyer lien d&apos;activation
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      className="rounded-full" 
                      onClick={async () => {
                        if (!window.confirm(`Envoyer un email de réinitialisation du mot de passe à ${selectedClient.email} ?`)) return;
                        try {
                          await companyApi.sendClientPasswordReset(selectedClient.id);
                          toast.success('Email de réinitialisation envoyé !');
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
                        }
                      }}
                      data-testid="send-password-reset-btn"
                    >
                      <Key className="w-4 h-4 mr-2" />
                      Réinitialiser mot de passe
                    </Button>
                  )}
                  <Button className="rounded-full" onClick={() => openBookingDialog()} data-testid="book-for-client-btn">
                    <Calendar className="w-4 h-4 mr-2" />
                    Réserver
                  </Button>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-green-600 font-medium">Chiffre d'affaires</p>
                    <p className="text-2xl font-bold text-green-700">
                      {(() => {
                        const totalCA = (selectedClient.bookings || []).reduce((sum, b) => {
                          return sum + (b.remaining_charge || b.invoice_amount || 0);
                        }, 0);
                        return `${totalCA.toFixed(0)}€`;
                      })()}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Interventions</p>
                    <p className="text-2xl font-bold">{selectedClient.bookings?.length || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Réalisés</p>
                    <p className="text-2xl font-bold">
                      {selectedClient.bookings?.filter(b => b.status === 'completed').length || 0}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Client depuis</p>
                    <p className="text-lg font-bold">
                      {selectedClient.created_at 
                        ? new Date(selectedClient.created_at).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })
                        : '-'
                      }
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Subscription Info */}
              {selectedClient.subscription && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Abonnement
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Statut</p>
                        <Badge className={selectedClient.subscription.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                          {selectedClient.subscription.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Fréquence</p>
                        <p className="font-semibold capitalize">
                          {selectedClient.subscription.frequency === 'monthly' ? 'Mensuel' :
                           selectedClient.subscription.frequency === 'biweekly' ? 'Bi-mensuel' :
                           selectedClient.subscription.frequency === 'weekly' ? 'Hebdomadaire' :
                           selectedClient.subscription.frequency || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Type</p>
                        <p className="font-semibold">{selectedClient.subscription.slot_type === 'half_day' ? 'Demi-journée' : 'Journée'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Tarif</p>
                        <p className="font-semibold">{selectedClient.subscription.price_per_hour || 35}€/h</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Passages/an</p>
                        <p className="font-semibold">{selectedClient.subscription.passages_per_year || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Notes */}
              {selectedClient.notes && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedClient.notes}</p>
                  </CardContent>
                </Card>
              )}

              {/* Interventions History with Invoice */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Historique des interventions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(!selectedClient.bookings || selectedClient.bookings.length === 0) ? (
                    <p className="text-sm text-muted-foreground text-center py-4">Aucune intervention</p>
                  ) : (
                    <div className="space-y-3">
                      {selectedClient.bookings.map((booking) => (
                        <div key={booking.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                          <div className="flex-1">
                            <p className="font-medium">
                              {new Date(booking.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-muted-foreground">
                                {booking.slot_type === 'half_day' ? 'Demi-journée' : 'Journée'}
                              </span>
                              {booking.remaining_charge && (
                                <span className="text-xs font-medium text-primary">{booking.remaining_charge.toFixed(2)}€</span>
                              )}
                              {booking.booked_by_company && (
                                <Badge variant="outline" className="text-xs">Réservé par vous</Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={booking.status === 'completed' ? 'default' : booking.status === 'cancelled' ? 'destructive' : 'secondary'}>
                              {booking.status === 'completed' ? 'Terminé' : booking.status === 'confirmed' ? 'Confirmé' : booking.status === 'cancelled' ? 'Annulé' : booking.status}
                            </Badge>
                            {booking.status === 'completed' && (
                              booking.intervention?.invoice_url ? (
                                <a href={booking.intervention.invoice_url} target="_blank" rel="noopener noreferrer">
                                  <Button variant="outline" size="sm" className="gap-1">
                                    <Download className="w-3 h-3" />
                                    Facture
                                  </Button>
                                </a>
                              ) : (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="gap-1"
                                  onClick={() => {
                                    setSelectedIntervention(booking.intervention || { id: booking.id });
                                    setIsInvoiceOpen(true);
                                  }}
                                >
                                  <Upload className="w-3 h-3" />
                                  Déposer facture
                                </Button>
                              )
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Exchanges History */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageSquare className="w-5 h-5" />
                      Historique des échanges
                    </CardTitle>
                    <Button 
                      size="sm" 
                      className="rounded-full"
                      onClick={() => setIsExchangeOpen(true)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {exchanges.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Aucun échange enregistré
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {exchanges.map((exchange) => (
                        <div key={exchange.id} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg group">
                          <div className={`p-2 rounded-full ${
                            exchange.direction === 'incoming' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                          }`}>
                            {getExchangeIcon(exchange.exchange_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {getExchangeLabel(exchange.exchange_type)}
                              </Badge>
                              <Badge variant="secondary" className="text-xs">
                                {exchange.direction === 'incoming' ? 'Entrant' : 'Sortant'}
                              </Badge>
                              {exchange.duration_minutes && (
                                <span className="text-xs text-muted-foreground">
                                  {exchange.duration_minutes} min
                                </span>
                              )}
                            </div>
                            {exchange.subject && (
                              <p className="font-medium text-sm mt-1">{exchange.subject}</p>
                            )}
                            {exchange.content && (
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{exchange.content}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">
                              {new Date(exchange.created_at).toLocaleString('fr-FR')}
                            </p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="opacity-0 group-hover:opacity-100 h-8 w-8"
                            onClick={() => handleDeleteExchange(exchange.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Exchange Dialog */}
      <Dialog open={isExchangeOpen} onOpenChange={setIsExchangeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Nouvel échange</DialogTitle>
            <DialogDescription>Enregistrez un échange avec {selectedClient?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={newExchange.exchange_type} onValueChange={(v) => setNewExchange({...newExchange, exchange_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Appel téléphonique</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="meeting">Réunion</SelectItem>
                    <SelectItem value="note">Note interne</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Direction</Label>
                <Select value={newExchange.direction} onValueChange={(v) => setNewExchange({...newExchange, direction: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outgoing">Sortant (vous → client)</SelectItem>
                    <SelectItem value="incoming">Entrant (client → vous)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newExchange.exchange_type === 'call' && (
              <div className="space-y-2">
                <Label>Durée (minutes)</Label>
                <Input 
                  type="number" 
                  value={newExchange.duration_minutes} 
                  onChange={(e) => setNewExchange({...newExchange, duration_minutes: e.target.value})}
                  placeholder="5"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Sujet</Label>
              <Input 
                value={newExchange.subject} 
                onChange={(e) => setNewExchange({...newExchange, subject: e.target.value})}
                placeholder="Ex: Confirmation de RDV"
              />
            </div>
            <div className="space-y-2">
              <Label>Contenu / Notes</Label>
              <Textarea 
                value={newExchange.content} 
                onChange={(e) => setNewExchange({...newExchange, content: e.target.value})}
                placeholder="Résumé de l'échange..."
                rows={3}
              />
            </div>
            <Button className="w-full rounded-full" onClick={handleAddExchange}>
              Enregistrer l'échange
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking for Client Dialog - Enhanced with Available Slots */}
      <Dialog open={isBookingOpen} onOpenChange={setIsBookingOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Réserver pour {selectedClient?.name}</DialogTitle>
            <DialogDescription>Sélectionnez un créneau disponible</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Week Navigation */}
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
                <ChevronRight className="w-4 h-4 rotate-180 mr-1" />
                Semaine préc.
              </Button>
              <span className="text-sm font-medium">
                {selectedWeekStart && `Semaine du ${selectedWeekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`}
              </span>
              <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
                Semaine suiv.
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Available Slots Grid */}
            {isLoadingSlots ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Chargement des créneaux...</p>
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-2">
                {availableSlots.slice(0, 10).map((slot) => (
                  <div key={slot.date} className="space-y-2">
                    {/* Day Header */}
                    <div className="text-center p-2 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground capitalize">{slot.dayName}</p>
                      <p className="font-medium">{slot.dayNum}</p>
                      <p className="text-xs text-muted-foreground">{slot.month}</p>
                    </div>
                    {/* Morning Slot */}
                    <button
                      onClick={() => handleSlotSelect(slot.date, 'half_day')}
                      className={`w-full p-2 rounded-lg border text-xs transition-all ${
                        bookingForm.date === slot.date && bookingForm.slot_type === 'half_day'
                          ? 'bg-primary text-white border-primary'
                          : slot.available_morning
                            ? 'bg-green-50 border-green-200 hover:bg-green-100 text-green-700'
                            : 'bg-red-50 border-red-200 text-red-400 cursor-not-allowed'
                      }`}
                      disabled={!slot.available_morning}
                    >
                      Matin
                    </button>
                    {/* Afternoon Slot */}
                    <button
                      onClick={() => handleSlotSelect(slot.date, 'full_day')}
                      className={`w-full p-2 rounded-lg border text-xs transition-all ${
                        bookingForm.date === slot.date && bookingForm.slot_type === 'full_day'
                          ? 'bg-primary text-white border-primary'
                          : slot.available_afternoon
                            ? 'bg-green-50 border-green-200 hover:bg-green-100 text-green-700'
                            : 'bg-red-50 border-red-200 text-red-400 cursor-not-allowed'
                      }`}
                      disabled={!slot.available_afternoon}
                    >
                      Journée
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Selected Slot Info */}
            {bookingForm.date && (
              <div className="p-3 bg-primary/10 rounded-lg">
                <p className="text-sm font-medium">
                  Créneau sélectionné : {new Date(bookingForm.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} - {bookingForm.slot_type === 'half_day' ? 'Demi-journée (4h)' : 'Journée complète (7h)'}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Collaborateur (optionnel)</Label>
                <Select value={bookingForm.collaborator_id || 'none'} onValueChange={(v) => setBookingForm({...bookingForm, collaborator_id: v === 'none' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non assigné</SelectItem>
                    {collaborators.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Motif de la réservation *</Label>
                <Input value={bookingForm.reason} onChange={(e) => setBookingForm({...bookingForm, reason: e.target.value})} placeholder="Ex: Tonte urgente..." />
              </div>
            </div>
            <Button className="w-full rounded-full" onClick={handleCreateBooking} disabled={isSubmitting || !bookingForm.date}>
              {isSubmitting ? 'Création...' : 'Confirmer la réservation'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Deposit Dialog */}
      <Dialog open={isInvoiceOpen} onOpenChange={setIsInvoiceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Déposer une facture</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Fichier PDF de la facture *</Label>
              <Input 
                type="file" 
                accept=".pdf"
                onChange={(e) => setInvoiceForm({...invoiceForm, file: e.target.files[0]})}
                data-testid="invoice-file-input"
              />
              {invoiceForm.file && (
                <p className="text-sm text-muted-foreground">Fichier: {invoiceForm.file.name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Montant de la facture (€) *</Label>
              <Input type="number" step="0.01" value={invoiceForm.invoice_amount} onChange={(e) => setInvoiceForm({...invoiceForm, invoice_amount: e.target.value})} placeholder="150.00" data-testid="invoice-amount-input" />
            </div>
            <Button className="w-full rounded-full" onClick={handleDepositInvoice} disabled={isSubmitting} data-testid="submit-invoice-btn">
              {isSubmitting ? 'Dépôt en cours...' : 'Déposer la facture'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier le client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nom</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Téléphone</Label>
              <Input value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Textarea value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} placeholder="Notes internes..." />
            </div>
            <Button className="w-full rounded-full" onClick={handleSaveEdit}>
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// MRR Dashboard Page
function MRRDashboardPage() {
  const navigate = useNavigate();
  const [mrrData, setMrrData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchMRR = async () => {
      try {
        const response = await companyApi.getMRR();
        setMrrData(response.data);
      } catch (error) {
        toast.error('Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    };
    fetchMRR();
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted rounded w-1/3 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Revenus Mensuels (MRR)</h1>
        <p className="text-muted-foreground mt-1">Suivez vos revenus récurrents mensuels</p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">MRR Total</p>
                <p className="text-4xl font-heading font-bold text-primary mt-1">
                  {mrrData?.total_mrr?.toFixed(0) || 0}€
                </p>
              </div>
              <div className="w-14 h-14 bg-primary/20 rounded-xl flex items-center justify-center">
                <Euro className="w-7 h-7 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Abonnés actifs</p>
                <p className="text-3xl font-heading font-semibold mt-1">{mrrData?.active_subscriptions || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Nouveaux ce mois</p>
                <p className="text-3xl font-heading font-semibold mt-1 flex items-center gap-2">
                  {mrrData?.new_this_month || 0}
                  {mrrData?.new_this_month > 0 && (
                    <ArrowUpRight className="w-5 h-5 text-green-500" />
                  )}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Revenu moyen/client</p>
                <p className="text-3xl font-heading font-semibold mt-1">
                  {mrrData?.avg_revenue_per_client?.toFixed(0) || 0}€
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Churn Alert */}
      {mrrData?.churned_this_month > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 flex items-center gap-3">
            <ArrowDownRight className="w-5 h-5 text-red-600" />
            <div>
              <p className="font-medium text-red-800">Attention : {mrrData.churned_this_month} désabonnement(s) ce mois</p>
              <p className="text-sm text-red-700">Pensez à contacter ces clients pour comprendre leurs besoins.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscriptions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Abonnements actifs
          </CardTitle>
          <CardDescription>Liste détaillée de vos abonnements en cours</CardDescription>
        </CardHeader>
        <CardContent>
          {!mrrData?.subscriptions?.length ? (
            <div className="text-center py-8 text-muted-foreground">
              <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Aucun abonnement actif</p>
              <p className="text-sm mt-1">Les demandes approuvées apparaîtront ici</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Fréquence</TableHead>
                  <TableHead>Crédit total</TableHead>
                  <TableHead>Crédit restant</TableHead>
                  <TableHead className="text-right">Mensualité</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mrrData.subscriptions.map((sub) => (
                  <TableRow key={sub.id}>
                    <TableCell>
                      <button 
                        className="text-left hover:text-primary transition-colors"
                        onClick={() => navigate(`/company/clients?highlight=${sub.client_id}`)}
                      >
                        <p className="font-medium hover:underline">{sub.client_name || 'Client'}</p>
                        {sub.client_email && (
                          <p className="text-xs text-muted-foreground">{sub.client_email}</p>
                        )}
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {sub.frequency === 'weekly' ? 'Hebdomadaire' : 
                         sub.frequency === 'biweekly' ? 'Bi-mensuel' :
                         sub.frequency === 'monthly' ? 'Mensuel' :
                         sub.frequency === 'quarterly' ? 'Trimestriel' :
                         sub.frequency || 'Personnalisé'}
                      </Badge>
                    </TableCell>
                    <TableCell>{sub.total_credits || 0}h</TableCell>
                    <TableCell>
                      <span className={sub.remaining_credits < 10 ? 'text-amber-600 font-medium' : ''}>
                        {sub.remaining_credits || 0}h
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {sub.monthly_payment?.toFixed(2) || sub.monthly_price?.toFixed(2) || 0}€
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4 flex items-start gap-3">
          <MessageSquare className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">À propos du MRR</p>
            <p className="text-sm text-blue-700">
              Le MRR (Revenu Mensuel Récurrent) représente vos revenus mensuels récurrents basés sur les abonnements actifs. 
              C'est un indicateur clé de la santé financière de votre activité.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Completed Interventions Page (for invoice and photo management)
function CompletedInterventionsPage() {
  const [interventions, setInterventions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [isInterventionDetailOpen, setIsInterventionDetailOpen] = useState(false);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [isPhotoDialogOpen, setIsPhotoDialogOpen] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  
  // Photo upload states
  const [photoFile, setPhotoFile] = useState(null);
  const [photoType, setPhotoType] = useState('after');
  const [photoCaption, setPhotoCaption] = useState('');
  const [shareWithClient, setShareWithClient] = useState(true);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  
  // Validation states
  const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false);
  const [validationMode, setValidationMode] = useState('client'); // 'client' or 'company'
  const [validationNote, setValidationNote] = useState('');
  const [validationType, setValidationType] = useState('phone'); // 'phone', 'email', 'sms', 'other'
  const [isValidating, setIsValidating] = useState(false);

  // Get collaborators for display
  const [collaborators, setCollaborators] = useState([]);

  const fetchInterventions = async () => {
    try {
      const response = await companyApi.getCompletedInterventions();
      setInterventions(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCollaborators = async () => {
    try {
      const response = await companyApi.getCollaborators();
      setCollaborators(response.data);
    } catch (error) {
      // Ignore error
    }
  };

  useEffect(() => { 
    fetchInterventions(); 
    fetchCollaborators();
  }, []);

  // Get collaborator names for an intervention
  const getInterventionCollaborators = (intervention) => {
    if (intervention.collaborator_ids?.length > 0) {
      return collaborators.filter(c => intervention.collaborator_ids.includes(c.id));
    }
    if (intervention.collaborator_id) {
      return collaborators.filter(c => c.id === intervention.collaborator_id);
    }
    return [];
  };

  // Handle validation by company (on behalf of client)
  const handleValidateByCompany = async () => {
    if (!selectedIntervention) return;
    if (!validationNote.trim()) {
      toast.error('Veuillez ajouter une note explicative');
      return;
    }
    
    setIsValidating(true);
    try {
      await companyApi.validateInterventionByCompany(selectedIntervention.id, {
        validation_type: validationType,
        note: validationNote,
        validated_at: new Date().toISOString()
      });
      toast.success('Validation enregistrée');
      setIsValidationDialogOpen(false);
      setValidationNote('');
      fetchInterventions();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    } finally {
      setIsValidating(false);
    }
  };

  const handleOpenInvoiceDialog = (intervention) => {
    setSelectedIntervention(intervention);
    setInvoiceFile(null);
    setInvoiceAmount(intervention.invoice_amount || '');
    setIsInvoiceDialogOpen(true);
  };

  const handleOpenPhotoDialog = (intervention) => {
    setSelectedIntervention(intervention);
    setPhotoFile(null);
    setPhotoType('after');
    setPhotoCaption('');
    setShareWithClient(true);
    setIsPhotoDialogOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Seuls les fichiers PDF sont acceptés');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Le fichier ne doit pas dépasser 5 Mo');
        return;
      }
      setInvoiceFile(file);
    }
  };

  const handlePhotoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
      if (!validTypes.includes(file.type)) {
        toast.error('Format non supporté. Utilisez JPG, PNG ou WebP.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Le fichier ne doit pas dépasser 10 Mo');
        return;
      }
      setPhotoFile(file);
    }
  };

  const handleSaveInvoice = async () => {
    if (!invoiceFile) {
      toast.error('Veuillez sélectionner un fichier PDF');
      return;
    }
    setIsUploading(true);
    try {
      await companyApi.uploadInvoiceFile(
        selectedIntervention.id, 
        invoiceFile, 
        invoiceAmount ? parseFloat(invoiceAmount) : null
      );
      toast.success('Facture uploadée avec succès');
      setIsInvoiceDialogOpen(false);
      fetchInterventions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadPhoto = async () => {
    if (!photoFile) {
      toast.error('Veuillez sélectionner une photo');
      return;
    }
    setIsUploadingPhoto(true);
    try {
      await companyApi.uploadInterventionPhoto(
        selectedIntervention.id,
        photoFile,
        photoType,
        photoCaption || null,
        shareWithClient
      );
      toast.success('Photo uploadée et compressée avec succès');
      setPhotoFile(null);
      setPhotoCaption('');
      fetchInterventions();
      // Refresh selected intervention
      const response = await companyApi.getCompletedInterventions();
      const updated = response.data.find(i => i.id === selectedIntervention.id);
      if (updated) setSelectedIntervention(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (interventionId, photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await companyApi.deleteInterventionPhoto(interventionId, photoId);
      toast.success('Photo supprimée');
      fetchInterventions();
      // Refresh selected intervention
      const response = await companyApi.getCompletedInterventions();
      const updated = response.data.find(i => i.id === interventionId);
      if (updated) setSelectedIntervention(updated);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTogglePhotoShare = async (interventionId, photo) => {
    try {
      await companyApi.updateInterventionPhoto(interventionId, photo.id, {
        share_with_client: !photo.share_with_client
      });
      toast.success(photo.share_with_client ? 'Photo masquée au client' : 'Photo visible pour le client');
      fetchInterventions();
      // Refresh selected intervention
      const response = await companyApi.getCompletedInterventions();
      const updated = response.data.find(i => i.id === interventionId);
      if (updated) setSelectedIntervention(updated);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteInvoice = async (interventionId) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await companyApi.deleteInvoice(interventionId);
      toast.success('Facture supprimée');
      fetchInterventions();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Get photos count by type
  const getPhotoCounts = (intervention) => {
    const photos = intervention.intervention_photos || [];
    const before = photos.filter(p => p.photo_type === 'before').length;
    const after = photos.filter(p => p.photo_type === 'after').length;
    return { before, after, total: photos.length };
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Prestations Terminées</h1>
        <p className="text-muted-foreground mt-1">Gérez les photos et factures des interventions réalisées</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{interventions.length}</p>
              <p className="text-xs text-muted-foreground">Interventions terminées</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Image className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{interventions.reduce((sum, i) => sum + (i.intervention_photos?.length || 0), 0)}</p>
              <p className="text-xs text-muted-foreground">Photos uploadées</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{interventions.filter(i => i.invoice_url).length}</p>
              <p className="text-xs text-muted-foreground">Factures déposées</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{interventions.filter(i => !i.invoice_url).length}</p>
              <p className="text-xs text-muted-foreground">En attente de facture</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Interventions Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center">Chargement...</div>
          ) : interventions.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Aucune intervention terminée</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Intervenants</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Photos</TableHead>
                  <TableHead>Facture</TableHead>
                  <TableHead>
                    <span className="flex items-center gap-1">
                      Validation client
                      <span className="text-xs text-muted-foreground" title="Le client peut valider l'intervention depuis son espace">ⓘ</span>
                    </span>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {interventions.map((intervention) => {
                  // Get collaborators for this intervention
                  const interventionCollabs = intervention.collaborator_ids?.length > 0
                    ? collaborators.filter(c => intervention.collaborator_ids.includes(c.id))
                    : intervention.collaborator_id
                    ? collaborators.filter(c => c.id === intervention.collaborator_id)
                    : [];
                  const photoCounts = getPhotoCounts(intervention);
                  return (
                  <TableRow 
                    key={intervention.id} 
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => {
                      setSelectedIntervention(intervention);
                      setIsInterventionDetailOpen(true);
                    }}
                  >
                    <TableCell>
                      <p className="font-medium">
                        {intervention.booking?.date ? 
                          new Date(intervention.booking.date).toLocaleDateString('fr-FR', { 
                            day: 'numeric', month: 'short', year: 'numeric' 
                          }) : 
                          new Date(intervention.completed_at).toLocaleDateString('fr-FR', { 
                            day: 'numeric', month: 'short', year: 'numeric' 
                          })
                        }
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {intervention.booking_type === 'punctual' ? 'Ponctuel' : 'Abonnement'}
                      </p>
                    </TableCell>
                    <TableCell>
                      {intervention.client ? (
                        <div>
                          <p className="font-medium">{intervention.client.name}</p>
                          <p className="text-xs text-muted-foreground">{intervention.client.email}</p>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Collaborators who were on this intervention */}
                      {interventionCollabs.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {interventionCollabs.map(collab => (
                            <div 
                              key={collab.id} 
                              className="flex items-center gap-1 bg-muted/50 rounded-full px-2 py-0.5"
                              title={collab.name}
                            >
                              {collab.avatar_url ? (
                                <img 
                                  src={getAssetUrl(collab.avatar_url)}
                                  alt={collab.name}
                                  className="w-5 h-5 rounded-full object-cover" loading="lazy" decoding="async"
                                />
                              ) : (
                                <div 
                                  className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-medium"
                                  style={{ backgroundColor: collab.color || '#6366f1' }}
                                >
                                  {collab.name?.charAt(0)?.toUpperCase()}
                                </div>
                              )}
                              <span className="text-xs font-medium truncate max-w-[60px]">{collab.name?.split(' ')[0]}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">Non assigné</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {intervention.booking?.slot_type === 'half_day' ? 'Demi-journée' : 
                       intervention.booking?.slot_type === 'full_day' ? 'Journée' : 
                       intervention.booking?.service_name || '-'}
                    </TableCell>
                    <TableCell>
                      {photoCounts.total > 0 ? (
                        <Badge 
                          variant="secondary" 
                          className="gap-1 cursor-pointer hover:bg-secondary/80" 
                          onClick={() => handleOpenPhotoDialog(intervention)}
                        >
                          <Image className="w-3 h-3" />
                          {photoCounts.before > 0 && <span className="text-blue-600">{photoCounts.before} avant</span>}
                          {photoCounts.before > 0 && photoCounts.after > 0 && ' / '}
                          {photoCounts.after > 0 && <span className="text-green-600">{photoCounts.after} après</span>}
                          {photoCounts.before === 0 && photoCounts.after === 0 && photoCounts.total}
                        </Badge>
                      ) : (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-muted-foreground h-7 px-2" 
                          onClick={() => handleOpenPhotoDialog(intervention)}
                        >
                          <Camera className="w-4 h-4 mr-1" />
                          Ajouter
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      {intervention.invoice_url ? (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-green-100 text-green-700 border-green-200 gap-1">
                            <FileText className="w-3 h-3" />
                            {intervention.invoice_amount ? `${intervention.invoice_amount}€` : 'PDF'}
                          </Badge>
                          <a 
                            href={`${getAssetUrl(intervention.invoice_url)}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                            title="Télécharger la facture"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        </div>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          En attente
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {intervention.client_validation_status === 'validated' ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200 gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Validé
                        </Badge>
                      ) : intervention.client_validation_status === 'disputed' ? (
                        <Badge className="bg-red-100 text-red-700 border-red-200 gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Litige
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">
                          En attente
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenPhotoDialog(intervention)}
                          title="Gérer les photos"
                        >
                          <Camera className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenInvoiceDialog(intervention)}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          {intervention.invoice_url ? 'Modifier' : 'Facture'}
                        </Button>
                        {intervention.invoice_url && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDeleteInvoice(intervention.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )})}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Invoice Upload Dialog */}
      <Dialog open={isInvoiceDialogOpen} onOpenChange={setIsInvoiceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedIntervention?.invoice_url ? 'Modifier la facture' : 'Déposer une facture'}
            </DialogTitle>
            <DialogDescription>
              Uploadez votre facture au format PDF (max 5 Mo)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Show existing invoice if any */}
            {selectedIntervention?.invoice_url && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-medium text-green-700 mb-2">Facture actuelle :</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-green-600" />
                    <span className="text-sm">
                      {selectedIntervention.invoice_amount 
                        ? `Facture de ${selectedIntervention.invoice_amount}€`
                        : 'Facture PDF'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={`${getAssetUrl(selectedIntervention.invoice_url)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-700 hover:text-green-900"
                    >
                      <Download className="w-5 h-5" />
                    </a>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-800 h-7 px-2"
                      onClick={() => handleDeleteInvoice(selectedIntervention.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>{selectedIntervention?.invoice_url ? 'Remplacer par un nouveau fichier' : 'Fichier PDF *'}</Label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="invoice-file-input"
                />
                <label htmlFor="invoice-file-input" className="cursor-pointer">
                  {invoiceFile ? (
                    <div className="flex items-center justify-center gap-2 text-primary">
                      <FileText className="w-6 h-6" />
                      <span className="font-medium">{invoiceFile.name}</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Cliquez pour sélectionner un fichier PDF
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Taille max: 5 Mo
                      </p>
                    </div>
                  )}
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Montant (optionnel)</Label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={invoiceAmount}
                  onChange={(e) => setInvoiceAmount(e.target.value)}
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
              </div>
            </div>
            <Button 
              className="w-full rounded-full" 
              onClick={handleSaveInvoice}
              disabled={!invoiceFile || isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Upload en cours...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  {selectedIntervention?.invoice_url ? 'Remplacer la facture' : 'Uploader la facture'}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Management Dialog */}
      <Dialog open={isPhotoDialogOpen} onOpenChange={setIsPhotoDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Photos de l'intervention</DialogTitle>
            <DialogDescription>
              {selectedIntervention?.client?.name && `Client: ${selectedIntervention.client.name}`}
              {selectedIntervention?.booking?.date && ` - ${new Date(selectedIntervention.booking.date).toLocaleDateString('fr-FR')}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Before/After Photo Comparison */}
            {selectedIntervention?.intervention_photos?.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Photos de l'intervention
                </h4>
                
                {/* Check if we have both before and after photos */}
                {(() => {
                  const beforePhotos = selectedIntervention.intervention_photos.filter(p => p.photo_type === 'before');
                  const afterPhotos = selectedIntervention.intervention_photos.filter(p => p.photo_type === 'after');
                  
                  if (beforePhotos.length > 0 && afterPhotos.length > 0) {
                    // Show side-by-side comparison
                    return (
                      <div className="grid grid-cols-2 gap-4 bg-muted/30 p-4 rounded-xl">
                        <div className="space-y-2">
                          <Badge className="bg-blue-100 text-blue-700 w-full justify-center py-1">
                            AVANT
                          </Badge>
                          <div className="relative">
                            <img 
                              src={`${getAssetUrl(beforePhotos[0].url)}`}
                              alt="Avant intervention"
                              className="w-full h-48 object-cover rounded-lg border-2 border-blue-200"
                            />
                            <div className="absolute bottom-2 right-2 flex gap-1">
                              <Button 
                                variant="secondary" 
                                size="icon" 
                                className="w-6 h-6 opacity-80 hover:opacity-100"
                                onClick={() => handleTogglePhotoShare(selectedIntervention.id, beforePhotos[0])}
                              >
                                {beforePhotos[0].share_with_client ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-center text-muted-foreground">
                            {beforePhotos[0].share_with_client ? '✓ Visible client' : '✗ Non partagé'}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Badge className="bg-green-100 text-green-700 w-full justify-center py-1">
                            APRÈS
                          </Badge>
                          <div className="relative">
                            <img 
                              src={`${getAssetUrl(afterPhotos[0].url)}`}
                              alt="Après intervention"
                              className="w-full h-48 object-cover rounded-lg border-2 border-green-200"
                            />
                            <div className="absolute bottom-2 right-2 flex gap-1">
                              <Button 
                                variant="secondary" 
                                size="icon" 
                                className="w-6 h-6 opacity-80 hover:opacity-100"
                                onClick={() => handleTogglePhotoShare(selectedIntervention.id, afterPhotos[0])}
                              >
                                {afterPhotos[0].share_with_client ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-center text-muted-foreground">
                            {afterPhotos[0].share_with_client ? '✓ Visible client' : '✗ Non partagé'}
                          </p>
                        </div>
                      </div>
                    );
                  }
                  
                  // Show all photos in grid if not matching before/after
                  return (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {selectedIntervention.intervention_photos.map((photo) => (
                        <div key={photo.id} className="relative group">
                          <img 
                            src={`${getAssetUrl(photo.url)}`}
                            alt={photo.caption || 'Photo intervention'}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <div className="absolute top-2 left-2">
                            <Badge className={photo.photo_type === 'before' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                              {photo.photo_type === 'before' ? 'Avant' : 'Après'}
                            </Badge>
                          </div>
                          <div className="absolute top-2 right-2 flex gap-1">
                            <Button 
                              variant="secondary" 
                              size="icon" 
                              className="w-7 h-7 opacity-80 hover:opacity-100"
                              onClick={() => handleTogglePhotoShare(selectedIntervention.id, photo)}
                              title={photo.share_with_client ? 'Masquer au client' : 'Partager avec le client'}
                            >
                              {photo.share_with_client ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="icon" 
                              className="w-7 h-7 opacity-80 hover:opacity-100"
                              onClick={() => handleDeletePhoto(selectedIntervention.id, photo.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          {photo.caption && (
                            <p className="text-xs text-muted-foreground mt-1 truncate">{photo.caption}</p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {photo.share_with_client ? '✓ Visible client' : '✗ Non partagé'}
                          </p>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Upload New Photo */}
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-medium text-sm">Ajouter une photo</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type de photo</Label>
                  <Select value={photoType} onValueChange={setPhotoType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="before">Avant intervention</SelectItem>
                      <SelectItem value="after">Après intervention</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Partager avec le client</Label>
                  <div className="flex items-center gap-2 h-10">
                    <Checkbox
                      id="share-with-client-check"
                      checked={shareWithClient}
                      onCheckedChange={(checked) => setShareWithClient(checked)}
                    />
                    <label htmlFor="share-with-client-check" className="text-sm">
                      Visible dans l'espace client
                    </label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Photo</Label>
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    onChange={handlePhotoFileChange}
                    className="hidden"
                    id="photo-file-input"
                  />
                  <label htmlFor="photo-file-input" className="cursor-pointer">
                    {photoFile ? (
                      <div className="flex items-center justify-center gap-2 text-primary">
                        <Image className="w-6 h-6" />
                        <span className="font-medium">{photoFile.name}</span>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Camera className="w-8 h-8 mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          Cliquez pour sélectionner une photo
                        </p>
                        <p className="text-xs text-muted-foreground">
                          JPG, PNG, WebP (max 10 Mo) - Compression automatique
                        </p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description (optionnel)</Label>
                <Input
                  placeholder="Ex: Vue d'ensemble du jardin"
                  value={photoCaption}
                  onChange={(e) => setPhotoCaption(e.target.value)}
                />
              </div>

              <Button 
                className="w-full rounded-full" 
                onClick={handleUploadPhoto}
                disabled={!photoFile || isUploadingPhoto}
              >
                {isUploadingPhoto ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Compression et upload...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Ajouter la photo
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Intervention Detail Dialog */}
      <Dialog open={isInterventionDetailOpen} onOpenChange={setIsInterventionDetailOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Détails de la prestation
            </DialogTitle>
            <DialogDescription>
              {selectedIntervention?.booking?.date && 
                `Réalisée le ${new Date(selectedIntervention.booking.date).toLocaleDateString('fr-FR', { 
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
                })}`
              }
            </DialogDescription>
          </DialogHeader>
          
          {selectedIntervention && (
            <div className="space-y-6 py-4">
              {/* Client Info */}
              <div className="p-4 bg-muted/30 rounded-xl space-y-2">
                <h4 className="font-semibold flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Client
                </h4>
                {selectedIntervention.client ? (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Nom</p>
                      <p className="font-medium">{selectedIntervention.client.name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Email</p>
                      <p className="font-medium">{selectedIntervention.client.email || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Téléphone</p>
                      <p className="font-medium">{selectedIntervention.client.phone || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Adresse</p>
                      <p className="font-medium">{selectedIntervention.client.address || '-'}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground">-</p>
                )}
              </div>

              {/* Intervention Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-muted/30 rounded-xl">
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium">
                    {selectedIntervention.booking_type === 'punctual' ? 'Ponctuel' : 'Abonnement'}
                  </p>
                </div>
                <div className="p-4 bg-muted/30 rounded-xl">
                  <p className="text-sm text-muted-foreground">Intervenants</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {getInterventionCollaborators(selectedIntervention).length > 0 ? (
                      getInterventionCollaborators(selectedIntervention).map(collab => (
                        <Badge key={collab.id} variant="secondary" className="text-xs">
                          {collab.name}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="space-y-2">
                <h4 className="font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Historique
                </h4>
                <div className="space-y-2 pl-4 border-l-2 border-muted">
                  {selectedIntervention.started_at && (
                    <div className="relative pl-4">
                      <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-blue-500 border-2 border-background" />
                      <p className="text-sm font-medium">Démarrage</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(selectedIntervention.started_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  )}
                  {selectedIntervention.completed_at && (
                    <div className="relative pl-4">
                      <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-green-500 border-2 border-background" />
                      <p className="text-sm font-medium">Terminée</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(selectedIntervention.completed_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  )}
                  {selectedIntervention.client_validation_at && (
                    <div className="relative pl-4">
                      <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-purple-500 border-2 border-background" />
                      <p className="text-sm font-medium">Validée par le client</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(selectedIntervention.client_validation_at).toLocaleString('fr-FR')}
                        {selectedIntervention.validated_by === 'company' && (
                          <span className="ml-2 text-amber-600">
                            (par l'entreprise - {selectedIntervention.validation_type})
                          </span>
                        )}
                      </p>
                      {selectedIntervention.validation_note && (
                        <p className="text-xs text-muted-foreground mt-1 italic">
                          "{selectedIntervention.validation_note}"
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Photos Before/After */}
              {selectedIntervention.intervention_photos?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Photos Avant / Après
                  </h4>
                  {(() => {
                    const beforePhotos = selectedIntervention.intervention_photos.filter(p => p.photo_type === 'before');
                    const afterPhotos = selectedIntervention.intervention_photos.filter(p => p.photo_type === 'after');
                    
                    if (beforePhotos.length > 0 || afterPhotos.length > 0) {
                      return (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Badge className="bg-blue-100 text-blue-700 w-full justify-center py-1">AVANT</Badge>
                            {beforePhotos.length > 0 ? (
                              <img 
                                src={`${getAssetUrl(beforePhotos[0].url)}`}
                                alt="Avant"
                                className="w-full h-48 object-cover rounded-lg border-2 border-blue-200"
                              />
                            ) : (
                              <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                                Pas de photo
                              </div>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Badge className="bg-green-100 text-green-700 w-full justify-center py-1">APRÈS</Badge>
                            {afterPhotos.length > 0 ? (
                              <img 
                                src={`${getAssetUrl(afterPhotos[0].url)}`}
                                alt="Après"
                                className="w-full h-48 object-cover rounded-lg border-2 border-green-200"
                              />
                            ) : (
                              <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                                Pas de photo
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}

              {/* Invoice */}
              {selectedIntervention.invoice_url && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <h4 className="font-semibold flex items-center gap-2 text-green-700">
                    <FileText className="w-4 h-4" />
                    Facture
                  </h4>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm">
                      {selectedIntervention.invoice_amount 
                        ? `Montant : ${selectedIntervention.invoice_amount}€`
                        : 'Facture PDF'}
                    </span>
                    <a
                      href={`${getAssetUrl(selectedIntervention.invoice_url)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-green-700 hover:underline"
                    >
                      <Download className="w-4 h-4" />
                      Télécharger
                    </a>
                  </div>
                </div>
              )}

              {/* Validation Section */}
              <div className="p-4 border rounded-xl space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Validation client
                </h4>
                
                {selectedIntervention.client_validation_status === 'validated' ? (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Validée</span>
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      Le {new Date(selectedIntervention.client_validation_at).toLocaleString('fr-FR')}
                      {selectedIntervention.validated_by === 'company' && ` (par l'entreprise)`}
                    </p>
                    {selectedIntervention.validation_note && (
                      <p className="text-sm text-muted-foreground mt-2 italic">
                        Note : {selectedIntervention.validation_note}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Le client n'a pas encore validé cette intervention.
                    </p>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setValidationNote('');
                        setValidationType('phone');
                        setIsValidationDialogOpen(true);
                      }}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Valider à la place du client
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Validation by Company Dialog */}
      <Dialog open={isValidationDialogOpen} onOpenChange={setIsValidationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Validation par l'entreprise</DialogTitle>
            <DialogDescription>
              Enregistrez la validation effectuée à la demande du client
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Vous validez cette intervention à la place du client. Une note explicative est obligatoire.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Type d'accord obtenu *</Label>
              <Select value={validationType} onValueChange={setValidationType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="phone">📞 Par téléphone</SelectItem>
                  <SelectItem value="email">📧 Par email</SelectItem>
                  <SelectItem value="sms">💬 Par SMS</SelectItem>
                  <SelectItem value="other">📝 Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Note explicative *</Label>
              <Textarea
                placeholder="Ex: Client appelé le 28/03 à 14h30, il confirme la bonne réalisation du travail..."
                value={validationNote}
                onChange={(e) => setValidationNote(e.target.value)}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                Décrivez comment le client a donné son accord (date, heure, circonstances)
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setIsValidationDialogOpen(false)}
              >
                Annuler
              </Button>
              <Button
                className="flex-1"
                onClick={handleValidateByCompany}
                disabled={!validationNote.trim() || isValidating}
              >
                {isValidating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Validation...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Confirmer la validation
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ServicesPage moved to ./pages/ServicesPage.js
// RequestsPage moved to ./pages/RequestsPage.js

// Availability/Planning Management
function PlanningPage() {
  const [availability, setAvailability] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newAvail, setNewAvail] = useState({ date: '', slots: [], collaborator_id: '' });

  const fetchData = async () => {
    try {
      const [availRes, collabRes] = await Promise.all([
        companyApi.getAvailability(),
        companyApi.getCollaborators()
      ]);
      setAvailability(availRes.data);
      setCollaborators(collabRes.data.filter(c => c.is_active));
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    if (!newAvail.date || newAvail.slots.length === 0) {
      toast.error('Date et créneaux sont obligatoires');
      return;
    }
    try {
      await companyApi.createAvailability(newAvail);
      toast.success('Disponibilité ajoutée');
      setIsDialogOpen(false);
      setNewAvail({ date: '', slots: [], collaborator_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    try {
      await companyApi.deleteAvailability(id);
      toast.success('Disponibilité supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const toggleSlot = (slot) => {
    if (newAvail.slots.includes(slot)) {
      setNewAvail({ ...newAvail, slots: newAvail.slots.filter(s => s !== slot) });
    } else {
      setNewAvail({ ...newAvail, slots: [...newAvail.slots, slot] });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Planning</h1>
          <p className="text-muted-foreground mt-1">Gérez vos disponibilités</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-availability-btn">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Nouvelle Disponibilité</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  data-testid="avail-date"
                  type="date"
                  value={newAvail.date}
                  onChange={(e) => setNewAvail({ ...newAvail, date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Créneaux *</Label>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="morning"
                      checked={newAvail.slots.includes('morning')}
                      onCheckedChange={() => toggleSlot('morning')}
                    />
                    <label htmlFor="morning" className="text-sm">Matin (8h-12h)</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="afternoon"
                      checked={newAvail.slots.includes('afternoon')}
                      onCheckedChange={() => toggleSlot('afternoon')}
                    />
                    <label htmlFor="afternoon" className="text-sm">Après-midi (12h-16h)</label>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Collaborateur (optionnel)</Label>
                <Select
                  value={newAvail.collaborator_id}
                  onValueChange={(value) => setNewAvail({ ...newAvail, collaborator_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les collaborateurs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Tous les collaborateurs</SelectItem>
                    {collaborators.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full rounded-full" data-testid="submit-avail-btn" onClick={handleCreate}>
                Ajouter la disponibilité
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Chargement...</div>
          ) : availability.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              Aucune disponibilité configurée. Ajoutez des créneaux pour permettre aux clients de réserver.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Créneaux</TableHead>
                  <TableHead>Collaborateur</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {availability.map((avail) => (
                  <TableRow key={avail.id}>
                    <TableCell className="font-medium">
                      {new Date(avail.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {avail.slots.map((slot) => (
                          <Badge key={slot} variant="secondary">
                            {slot === 'morning' ? 'Matin' : 'Après-midi'}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {collaborators.find(c => c.id === avail.collaborator_id)?.name || 'Tous'}
                    </TableCell>
                    <TableCell>
                      <Badge className={avail.is_booked ? 'status-warning' : 'status-success'}>
                        {avail.is_booked ? 'Réservé' : 'Disponible'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => handleDelete(avail.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// QuestionsPage moved to ./pages/QuestionsPage.js

// Widgets Management Page
function WidgetsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [widgets, setWidgets] = useState([]);
  const [services, setServices] = useState([]);
  const [families, setFamilies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [isFamilyDialogOpen, setIsFamilyDialogOpen] = useState(false);
  const [editingFamily, setEditingFamily] = useState(null);
  const [newWidget, setNewWidget] = useState({
    widget_type: 'punctual',
    name: '',
    description: '',
    booking_mode: 'approval',
    allow_payment: false,
    primary_color: '#2F5233',
    secondary_color: '#D97757',
    bg_color: '#F9F8F6',
    text_color: '#1a1a1a',
    enabled_services: [],
    terms_enabled: false,
    terms_link: '',
    terms_text: ''
  });

  const fetchData = async () => {
    try {
      const [widgetsRes, servicesRes, familiesRes] = await Promise.all([
        companyApi.getWidgets(),
        companyApi.getServices(),
        companyApi.getServiceFamilies()
      ]);
      setWidgets(widgetsRes.data);
      setServices(servicesRes.data);
      setFamilies(familiesRes.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const resetForm = () => {
    setNewWidget({
      widget_type: 'punctual',
      name: '',
      description: '',
      booking_mode: 'approval',
      allow_payment: false,
      primary_color: '#2F5233',
      secondary_color: '#D97757',
      bg_color: '#F9F8F6',
      text_color: '#1a1a1a',
      enabled_services: [],
      terms_enabled: false,
      terms_link: '',
      terms_text: ''
    });
    setEditingWidget(null);
  };

  // Handle family target widget update
  const handleFamilyTargetChange = async (familyId, targetWidgetType) => {
    try {
      const family = families.find(f => f.id === familyId);
      await companyApi.updateServiceFamily(familyId, {
        name: family.name,
        description: family.description,
        color: family.color,
        target_widget_type: targetWidgetType
      });
      toast.success('Configuration mise à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleCreate = async () => {
    if (!newWidget.name) {
      toast.error('Le nom est obligatoire');
      return;
    }

    try {
      if (editingWidget) {
        await companyApi.updateWidget(editingWidget.id, newWidget);
        toast.success('Widget mis à jour');
      } else {
        await companyApi.createWidget(newWidget);
        toast.success('Widget créé');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (widget) => {
    setEditingWidget(widget);
    setNewWidget({
      widget_type: widget.widget_type,
      name: widget.name,
      description: widget.description || '',
      booking_mode: widget.booking_mode,
      allow_payment: widget.allow_payment,
      primary_color: widget.primary_color,
      secondary_color: widget.secondary_color,
      bg_color: widget.bg_color,
      text_color: widget.text_color,
      enabled_services: widget.enabled_services || [],
      terms_enabled: widget.terms_enabled || false,
      terms_link: widget.terms_link || '',
      terms_text: widget.terms_text || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await companyApi.deleteWidget(id);
      toast.success('Widget supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDuplicate = async (widget) => {
    try {
      const duplicatedWidget = {
        widget_type: widget.widget_type,
        name: `${widget.name} (copie)`,
        description: widget.description || '',
        booking_mode: widget.booking_mode,
        allow_payment: widget.allow_payment,
        primary_color: widget.primary_color,
        secondary_color: widget.secondary_color,
        bg_color: widget.bg_color,
        text_color: widget.text_color,
        enabled_services: widget.enabled_services || [],
        terms_enabled: widget.terms_enabled || false,
        terms_link: widget.terms_link || '',
        terms_text: widget.terms_text || '',
        config: widget.config || {},
        show_logo: widget.show_logo,
        hourly_rate: widget.hourly_rate
      };
      await companyApi.createWidget(duplicatedWidget);
      toast.success('Widget dupliqué avec succès !');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const copyWidgetUrl = (widget) => {
    const publicUrl = process.env.REACT_APP_PUBLIC_URL || 'https://asset-deployment.preview.emergentagent.com';
    const widgetPath = getWidgetPath(widget);
    const url = `${publicUrl}/widget/${widgetPath}/${widget.id}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copiée !');
  };

  const copyIframeCode = (widget) => {
    const publicUrl = process.env.REACT_APP_PUBLIC_URL || 'https://asset-deployment.preview.emergentagent.com';
    const widgetPath = getWidgetPath(widget);
    const url = `${publicUrl}/widget/${widgetPath}/${widget.id}`;
    const iframe = `<iframe src="${url}" width="100%" height="700" frameborder="0" style="border: none; border-radius: 16px;"></iframe>`;
    navigator.clipboard.writeText(iframe);
    toast.success('Code iframe copié !');
  };
  
  const getWidgetPath = (widget) => {
    if (widget.widget_type === 'subscription') return 'subscription';
    if (widget.widget_type === 'recurring') return 'recurring';
    if (widget.widget_type === 'appointment') return 'appointment';
    if (widget.widget_type === 'dispatcher') return 'dispatcher';
    return 'punctual';
  };

  const getWidgetUrl = (widget) => {
    // "Voir" = aperçu DEPUIS l'environnement courant (preview ou prod).
    // On utilise window.location.origin pour éviter d'envoyer un admin preview vers la prod (et inversement).
    const origin = typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : (process.env.REACT_APP_PUBLIC_URL || 'https://asset-deployment.preview.emergentagent.com');
    const widgetPath = getWidgetPath(widget);
    return `${origin}/widget/${widgetPath}/${widget.id}`;
  };
  
  const getWidgetLabel = (type) => {
    switch(type) {
      case 'recurring':
      case 'subscription':
        return 'Abonnement';
      case 'appointment':
        return 'RDV';
      case 'dispatcher':
        return 'Dispatcher';
      case 'punctual':
      default:
        return 'Ponctuel';
    }
  };
  
  const getWidgetBadgeStyle = (type) => {
    switch(type) {
      case 'recurring':
      case 'subscription':
        return { backgroundColor: '#22c55e', color: 'white' }; // Vert
      case 'appointment':
        return { backgroundColor: '#3b82f6', color: 'white' }; // Bleu
      case 'dispatcher':
        return { backgroundColor: '#8b5cf6', color: 'white' }; // Violet
      case 'punctual':
      default:
        return { backgroundColor: '#f97316', color: 'white' }; // Orange
    }
  };
  
  const getWidgetIcon = (type) => {
    switch(type) {
      case 'recurring':
      case 'subscription':
        return Calendar;
      case 'appointment':
        return Clock;
      case 'dispatcher':
        return Layers;
      case 'punctual':
      default:
        return CalendarClock;
    }
  };

  return (
    <div className="space-y-6">
      <WidgetsGuideTour />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold" data-testid="widgets-page-title">Mes Widgets</h1>
          <p className="text-muted-foreground mt-1">Créez et gérez vos formulaires de réservation</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="create-widget-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Widget
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingWidget ? 'Modifier le widget' : 'Nouveau Widget'}
              </DialogTitle>
              <DialogDescription>
                Configurez votre formulaire de réservation en ligne
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Type de widget *</Label>
                <Select
                  value={newWidget.widget_type}
                  onValueChange={(value) => setNewWidget({ ...newWidget, widget_type: value })}
                  disabled={!!editingWidget}
                >
                  <SelectTrigger data-testid="widget-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="punctual">Demande ponctuelle</SelectItem>
                    <SelectItem value="recurring">Abonnement / Récurrent</SelectItem>
                    <SelectItem value="appointment">Prise de RDV</SelectItem>
                    <SelectItem value="dispatcher">Dispatcher (écran de choix)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {newWidget.widget_type === 'punctual' && "Pour des demandes de prestation à la demande"}
                  {newWidget.widget_type === 'recurring' && "Pour des abonnements avec prestations régulières"}
                  {newWidget.widget_type === 'appointment' && "Pour des rendez-vous physiques ou en visioconférence"}
                  {newWidget.widget_type === 'dispatcher' && "Écran de choix entre plusieurs types de demandes"}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Nom du widget *</Label>
                <Input
                  data-testid="widget-name"
                  value={newWidget.name}
                  onChange={(e) => setNewWidget({ ...newWidget, name: e.target.value })}
                  placeholder="Réservation jardinage"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  data-testid="widget-desc"
                  value={newWidget.description}
                  onChange={(e) => setNewWidget({ ...newWidget, description: e.target.value })}
                  placeholder="Description affichée aux clients..."
                />
              </div>

              {newWidget.widget_type === 'punctual' && (
                <>
                  <div className="space-y-2">
                    <Label>Mode de réservation</Label>
                    <Select
                      value={newWidget.booking_mode}
                      onValueChange={(value) => setNewWidget({ ...newWidget, booking_mode: value })}
                    >
                      <SelectTrigger data-testid="widget-booking-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="direct">Confirmation immédiate</SelectItem>
                        <SelectItem value="approval">Validation requise</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {newWidget.booking_mode === 'direct' 
                        ? 'Les réservations sont confirmées automatiquement'
                        : 'Vous devez approuver chaque demande de réservation'
                      }
                    </p>
                  </div>

                  <div className="flex items-center justify-between py-2">
                    <div>
                      <Label>Paiement en ligne</Label>
                      <p className="text-xs text-muted-foreground">Activer le paiement lors de la réservation</p>
                    </div>
                    <Switch
                      checked={newWidget.allow_payment}
                      onCheckedChange={(checked) => setNewWidget({ ...newWidget, allow_payment: checked })}
                      data-testid="widget-allow-payment"
                    />
                  </div>

                  {services.length > 0 && (
                    <div className="space-y-2">
                      <Label>Services disponibles</Label>
                      <p className="text-xs text-muted-foreground">Sélectionnez les services à afficher (tous si aucun sélectionné)</p>
                      <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                        {services.map((service) => (
                          <div key={service.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`service-${service.id}`}
                              checked={newWidget.enabled_services.includes(service.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setNewWidget({ ...newWidget, enabled_services: [...newWidget.enabled_services, service.id] });
                                } else {
                                  setNewWidget({ ...newWidget, enabled_services: newWidget.enabled_services.filter(id => id !== service.id) });
                                }
                              }}
                            />
                            <label htmlFor={`service-${service.id}`} className="text-sm">{service.name}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="border-t pt-4">
                <Label className="mb-3 block">Couleurs personnalisées</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Primaire</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={newWidget.primary_color}
                        onChange={(e) => setNewWidget({ ...newWidget, primary_color: e.target.value })}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={newWidget.primary_color}
                        onChange={(e) => setNewWidget({ ...newWidget, primary_color: e.target.value })}
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Secondaire</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={newWidget.secondary_color}
                        onChange={(e) => setNewWidget({ ...newWidget, secondary_color: e.target.value })}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={newWidget.secondary_color}
                        onChange={(e) => setNewWidget({ ...newWidget, secondary_color: e.target.value })}
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Fond</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={newWidget.bg_color}
                        onChange={(e) => setNewWidget({ ...newWidget, bg_color: e.target.value })}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={newWidget.bg_color}
                        onChange={(e) => setNewWidget({ ...newWidget, bg_color: e.target.value })}
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Texte</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={newWidget.text_color}
                        onChange={(e) => setNewWidget({ ...newWidget, text_color: e.target.value })}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={newWidget.text_color}
                        onChange={(e) => setNewWidget({ ...newWidget, text_color: e.target.value })}
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* CGV / Conditions */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="block">Conditions Générales de Vente (CGV)</Label>
                  <Switch
                    checked={newWidget.terms_enabled}
                    onCheckedChange={(checked) => setNewWidget({ ...newWidget, terms_enabled: checked })}
                    data-testid="terms-enabled-switch"
                  />
                </div>
                {newWidget.terms_enabled && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-xs">Lien vers vos CGV *</Label>
                      <Input
                        value={newWidget.terms_link || ''}
                        onChange={(e) => setNewWidget({ ...newWidget, terms_link: e.target.value })}
                        placeholder="https://www.votresite.fr/cgv"
                        data-testid="terms-link-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Texte de consentement (affiché au client)</Label>
                      <Textarea
                        value={newWidget.terms_text || ''}
                        onChange={(e) => setNewWidget({ ...newWidget, terms_text: e.target.value })}
                        placeholder="J'accepte les conditions générales de vente"
                        rows={2}
                        data-testid="terms-text-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground bg-amber-50 text-amber-700 p-2 rounded">
                      Le client devra cocher une case d'acceptation des CGV avant de soumettre sa demande.
                    </p>
                  </>
                )}
              </div>
              
              <Button className="w-full rounded-full" data-testid="submit-widget-btn" onClick={handleCreate}>
                {editingWidget ? 'Enregistrer' : 'Créer le widget'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Widget Dispatcher Section */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-500 flex-shrink-0 flex items-center justify-center">
              <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="min-w-0">
              <CardTitle className="font-heading text-blue-900 text-base sm:text-lg">Widget Dispatcher</CardTitle>
              <CardDescription className="text-blue-700 text-sm">
                Un seul widget qui redirige vers le bon formulaire
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-blue-800">
            Le <strong>Widget Dispatcher</strong> permet à vos visiteurs de choisir le type de service et les redirige automatiquement vers le bon widget de réservation.
          </p>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Code iframe */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-blue-900 text-sm">
                <Code className="w-4 h-4 flex-shrink-0" />
                Code iframe
              </Label>
              <div className="relative">
                <pre className="p-3 bg-white rounded-lg border border-blue-200 text-xs overflow-x-auto whitespace-pre-wrap break-all">
{`<iframe src="${process.env.REACT_APP_PUBLIC_URL || 'https://asset-deployment.preview.emergentagent.com'}/widget/dispatcher/${user?.company_id}" width="100%" height="600" frameborder="0"></iframe>`}
                </pre>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="absolute top-2 right-2 h-7 text-xs"
                  onClick={() => {
                    const publicUrl = process.env.REACT_APP_PUBLIC_URL || 'https://asset-deployment.preview.emergentagent.com';
                    const code = `<iframe src="${publicUrl}/widget/dispatcher/${user?.company_id}" width="100%" height="600" frameborder="0"></iframe>`;
                    navigator.clipboard.writeText(code);
                    toast.success('Code iframe copié !');
                  }}
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copier
                </Button>
              </div>
            </div>
            
            {/* Code script */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-blue-900 text-sm">
                <Code className="w-4 h-4 flex-shrink-0" />
                Code JavaScript (popup)
              </Label>
              <div className="relative">
                <pre className="p-3 bg-white rounded-lg border border-blue-200 text-xs overflow-x-auto whitespace-pre-wrap break-all">
{`<script>
function openResaWork() {
  window.open('${process.env.REACT_APP_PUBLIC_URL || 'https://asset-deployment.preview.emergentagent.com'}/widget/dispatcher/${user?.company_id}','ResaWork','width=500,height=700');
}
</script>
<button onclick="openResaWork()">Réserver</button>`}
                </pre>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="absolute top-2 right-2 h-7 text-xs"
                  onClick={() => {
                    const publicUrl = process.env.REACT_APP_PUBLIC_URL || 'https://asset-deployment.preview.emergentagent.com';
                    const code = `<script>function openResaWork(){window.open('${publicUrl}/widget/dispatcher/${user?.company_id}','ResaWork','width=500,height=700');}</script><button onclick="openResaWork()">Réserver</button>`;
                    navigator.clipboard.writeText(code);
                    toast.success('Code JavaScript copié !');
                  }}
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copier
                </Button>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button 
              variant="outline" 
              size="sm"
              className="text-blue-700 border-blue-300 hover:bg-blue-100 text-xs h-8"
              onClick={() => {
                const publicUrl = process.env.REACT_APP_PUBLIC_URL || 'https://asset-deployment.preview.emergentagent.com';
                const url = `${publicUrl}/widget/dispatcher/${user?.company_id}`;
                navigator.clipboard.writeText(url);
                toast.success('URL copiée !');
              }}
            >
              <Copy className="w-3 h-3 mr-1" />
              URL
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              className="text-blue-700 border-blue-300 hover:bg-blue-100 text-xs h-8"
              asChild
            >
              <a 
                href={`${window.location.origin}/widget/dispatcher/${user?.company_id}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                Prévisualiser
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Configuration des familles de services pour le dispatcher */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Settings className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="truncate">Configuration du Dispatcher</span>
              </CardTitle>
              <CardDescription className="mt-1">
                Personnalisez les options du dispatcher
              </CardDescription>
            </div>
            <Button 
              size="sm" 
              className="w-full sm:w-auto flex-shrink-0"
              onClick={() => {
                setEditingFamily(null);
                setIsFamilyDialogOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-1" />
              Ajouter une option
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {families.length > 0 ? (
            <div className="space-y-3">
              {families.map((family) => (
                <div 
                  key={family.id} 
                  className="p-3 sm:p-4 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  {/* Mobile: Stack layout */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    {/* Info section */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Image/Icon preview */}
                      <div 
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold overflow-hidden"
                        style={{ backgroundColor: family.color || '#22C55E' }}
                      >
                        {family.image_url ? (
                          <img 
                            src={family.image_url.startsWith('http') ? family.image_url : `${getAssetUrl(family.image_url)}`}
                            alt={family.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <span style={{ display: family.image_url ? 'none' : 'flex' }} className="text-sm sm:text-base">
                          {family.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{family.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{family.description || 'Aucune description'}</p>
                      </div>
                    </div>
                    
                    {/* Controls section - stack on mobile */}
                    <div className="flex items-center gap-2 sm:gap-3 mt-2 sm:mt-0">
                      <Select
                        value={family.target_widget_id || ''}
                        onValueChange={async (value) => {
                          const selectedWidget = widgets.find(w => w.id === value);
                          try {
                            await companyApi.updateServiceFamily(family.id, {
                              name: family.name,
                              description: family.description,
                              color: family.color,
                              target_widget_type: selectedWidget?.widget_type || family.target_widget_type,
                              target_widget_id: value,
                              image_url: family.image_url
                            });
                            toast.success('Widget mis à jour');
                            fetchData();
                          } catch (error) {
                            toast.error('Erreur lors de la mise à jour');
                          }
                        }}
                      >
                        <SelectTrigger className="w-full sm:w-48 text-xs sm:text-sm h-9">
                          <SelectValue placeholder="Choisir un widget">
                            {family.target_widget_id ? (
                              (() => {
                                const widget = widgets.find(w => w.id === family.target_widget_id);
                                return widget ? (
                                  <span className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${
                                      widget.widget_type === 'punctual' ? 'bg-orange-500' : 
                                      widget.widget_type === 'recurring' ? 'bg-green-500' : 'bg-blue-500'
                                    }`}></span>
                                    <span className="truncate">{widget.name}</span>
                                  </span>
                                ) : 'Widget supprimé';
                              })()
                            ) : 'Non configuré'}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {widgets.filter(w => w.is_active).map(widget => (
                            <SelectItem key={widget.id} value={widget.id}>
                              <span className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${
                                  widget.widget_type === 'punctual' ? 'bg-orange-500' : 
                                  widget.widget_type === 'recurring' ? 'bg-green-500' : 'bg-blue-500'
                                }`}></span>
                                {widget.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            setEditingFamily(family);
                            setIsFamilyDialogOpen(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-destructive h-8 w-8"
                          onClick={async () => {
                            if (confirm('Supprimer cette option ?')) {
                              await companyApi.deleteServiceFamily(family.id);
                              toast.success('Option supprimée');
                              fetchData();
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune option configurée</p>
              <p className="text-sm">Ajoutez des options pour que vos visiteurs puissent choisir leur type de service</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog pour éditer une famille */}
      <Dialog open={isFamilyDialogOpen} onOpenChange={setIsFamilyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingFamily ? 'Modifier l\'option' : 'Nouvelle option'}</DialogTitle>
            <DialogDescription>
              Personnalisez cette option du dispatcher
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input
                placeholder="Ex: Entretien, Aménagement..."
                value={editingFamily?.name || ''}
                onChange={(e) => setEditingFamily(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Sous-titre / Description</Label>
              <Input
                placeholder="Ex: Services d'entretien régulier"
                value={editingFamily?.description || ''}
                onChange={(e) => setEditingFamily(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Couleur</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  className="w-10 h-10 rounded cursor-pointer border"
                  value={editingFamily?.color || '#22C55E'}
                  onChange={(e) => setEditingFamily(prev => ({ ...prev, color: e.target.value }))}
                />
                <Input
                  className="flex-1"
                  value={editingFamily?.color || '#22C55E'}
                  onChange={(e) => setEditingFamily(prev => ({ ...prev, color: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Image / Icône (optionnel)</Label>
              <div className="flex items-center gap-3">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl overflow-hidden border-2 border-dashed"
                  style={{ backgroundColor: editingFamily?.color || '#22C55E', borderColor: editingFamily?.image_url ? 'transparent' : '#ccc' }}
                >
                  {editingFamily?.image_url ? (
                    <img 
                      src={editingFamily.image_url.startsWith('http') ? editingFamily.image_url : `${getAssetUrl(editingFamily.image_url)}`}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    editingFamily?.name?.charAt(0)?.toUpperCase() || '?'
                  )}
                </div>
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file && editingFamily?.id) {
                        try {
                          const res = await companyApi.uploadServiceFamilyImage(editingFamily.id, file);
                          setEditingFamily(prev => ({ ...prev, image_url: res.data.image_url }));
                          toast.success('Image uploadée');
                        } catch (error) {
                          toast.error('Erreur lors de l\'upload');
                        }
                      } else if (file && !editingFamily?.id) {
                        // Preview for new family (will upload after creation)
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          setEditingFamily(prev => ({ ...prev, image_preview: e.target.result, image_file: file }));
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="text-sm"
                  />
                  {editingFamily?.image_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive mt-1"
                      onClick={() => setEditingFamily(prev => ({ ...prev, image_url: null }))}
                    >
                      Supprimer l'image
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Redirige vers le widget</Label>
              <Select
                value={editingFamily?.target_widget_id || ''}
                onValueChange={(value) => {
                  const selectedWidget = widgets.find(w => w.id === value);
                  setEditingFamily(prev => ({ 
                    ...prev, 
                    target_widget_id: value,
                    target_widget_type: selectedWidget?.widget_type || 'punctual'
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez un widget" />
                </SelectTrigger>
                <SelectContent>
                  {widgets.filter(w => w.is_active).map(widget => (
                    <SelectItem key={widget.id} value={widget.id}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          widget.widget_type === 'punctual' ? 'bg-orange-500' : 
                          widget.widget_type === 'recurring' ? 'bg-green-500' : 'bg-blue-500'
                        }`}></span>
                        {widget.name}
                        <span className="text-xs text-muted-foreground">
                          ({widget.widget_type === 'punctual' ? 'Ponctuel' : 
                            widget.widget_type === 'recurring' ? 'Abonnement' : 'RDV'})
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                  {widgets.filter(w => w.is_active).length === 0 && (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      Aucun widget actif disponible
                    </div>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Sélectionnez le widget vers lequel vos clients seront redirigés
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsFamilyDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={async () => {
                try {
                  if (editingFamily?.id) {
                    // Update existing
                    await companyApi.updateServiceFamily(editingFamily.id, {
                      name: editingFamily.name,
                      description: editingFamily.description,
                      color: editingFamily.color,
                      target_widget_type: editingFamily.target_widget_type,
                      target_widget_id: editingFamily.target_widget_id,
                      image_url: editingFamily.image_url
                    });
                    toast.success('Option mise à jour');
                  } else {
                    // Create new
                    const res = await companyApi.createServiceFamily({
                      name: editingFamily?.name || 'Nouvelle option',
                      description: editingFamily?.description,
                      color: editingFamily?.color || '#22C55E',
                      target_widget_type: editingFamily?.target_widget_type || 'punctual',
                      target_widget_id: editingFamily?.target_widget_id
                    });
                    // Upload image if there's one pending
                    if (editingFamily?.image_file && res.data.id) {
                      await companyApi.uploadServiceFamilyImage(res.data.id, editingFamily.image_file);
                    }
                    toast.success('Option créée');
                  }
                  setIsFamilyDialogOpen(false);
                  setEditingFamily(null);
                  fetchData();
                } catch (error) {
                  toast.error('Erreur lors de la sauvegarde');
                }
              }}
              disabled={!editingFamily?.name}
            >
              {editingFamily?.id ? 'Enregistrer' : 'Créer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Liste des widgets créés par l'utilisateur */}
      <div className="space-y-4" data-testid="widget-list">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Layers className="w-5 h-5 text-primary" />
          Mes Widgets
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {isLoading ? (
          [...Array(2)].map((_, i) => <div key={i} className="h-40 bg-muted rounded-xl animate-pulse" />)
        ) : widgets.filter(w => w.is_active !== false && !w.is_demo && !w.is_default).length === 0 ? (
          <Card className="col-span-full border-dashed">
            <CardContent className="p-8 text-center text-muted-foreground">
              <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">Aucun widget personnalisé</p>
              <p className="text-sm mt-1">Cliquez sur "Nouveau Widget" pour créer votre premier widget de réservation</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {widgets.filter(w => !w.is_demo && !w.is_default).map((widget) => (
            <Card key={widget.id} className={`card-hover ${widget.is_active === false ? 'border-rose-200 bg-rose-50/30 opacity-90' : 'border-primary/30'}`}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${widget.primary_color || '#2F5233'}20` }}
                      >
                        {(() => {
                          const IconComponent = getWidgetIcon(widget.widget_type);
                          return <IconComponent className="w-5 h-5" style={{ color: widget.primary_color || '#2F5233' }} />;
                        })()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{widget.name}</p>
                        {/* ID badge directly under the name */}
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">ID</span>
                          <code className="text-[10px] font-mono text-muted-foreground/80 select-all truncate max-w-[180px]" data-testid={`widget-id-${widget.id}`}>
                            {widget.id}
                          </code>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(widget.id); toast.success('ID copié'); }}
                            className="text-muted-foreground hover:text-foreground p-0.5"
                            title="Copier l'ID"
                            data-testid={`widget-copy-id-${widget.id}`}
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap mt-1.5">
                          <Badge 
                            className="text-xs"
                            style={getWidgetBadgeStyle(widget.widget_type)}
                          >
                            {getWidgetLabel(widget.widget_type)}
                          </Badge>
                          {widget.is_active === false ? (
                            <Badge variant="outline" className="text-xs text-rose-700 border-rose-300 bg-rose-50 flex items-center gap-1" data-testid={`widget-status-${widget.id}`}>
                              <span className="w-2 h-2 bg-rose-500 rounded-full"></span>
                              Inactif
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-300 bg-emerald-50 flex items-center gap-1" data-testid={`widget-status-${widget.id}`}>
                              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                              Actif
                            </Badge>
                          )}
                          {widget.integration_url && (
                            <Badge variant="outline" className="text-xs text-green-600 border-green-300 bg-green-50 flex items-center gap-1">
                              <ExternalLink className="w-2.5 h-2.5" />
                              Intégré
                            </Badge>
                          )}
                        </div>
                      </div>
                      {/* Quick toggle Activate/Deactivate */}
                      <button
                        type="button"
                        onClick={async () => {
                          const newState = widget.is_active === false ? true : false;
                          const verb = newState ? 'activer' : 'désactiver';
                          if (!window.confirm(`Voulez-vous ${verb} ce widget ?${newState ? '\n\nIl redeviendra accessible publiquement.' : '\n\nIl renverra "Service indisponible" aux clients.'}`)) return;
                          try {
                            await companyApi.toggleWidgetActive(widget.id, newState);
                            toast.success(newState ? 'Widget activé' : 'Widget désactivé');
                            fetchData();
                          } catch (e) {
                            toast.error('Erreur lors du changement de statut');
                          }
                        }}
                        className={`text-xs px-2 py-1 rounded-md border whitespace-nowrap ${widget.is_active === false ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-rose-200 text-rose-600 hover:bg-rose-50'}`}
                        data-testid={`widget-toggle-${widget.id}`}
                      >
                        {widget.is_active === false ? 'Activer' : 'Désactiver'}
                      </button>
                    </div>
                    {widget.integration_name && (
                      <p className="text-xs text-muted-foreground mb-1">
                        Intégré sur : <span className="font-medium">{widget.integration_name}</span>
                        {widget.integration_url && (
                          <a href={widget.integration_url} target="_blank" rel="noopener noreferrer" className="ml-1 text-primary hover:underline">
                            <ExternalLink className="w-3 h-3 inline" />
                          </a>
                        )}
                      </p>
                    )}
                    {widget.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{widget.description}</p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => copyWidgetUrl(widget)}>
                        <Copy className="w-3 h-3 mr-1" />
                        URL
                      </Button>
                      <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => copyIframeCode(widget)}>
                        <Code className="w-3 h-3 mr-1" />
                        Iframe
                      </Button>
                      <Button variant="outline" size="sm" className="text-xs h-8" asChild>
                        <a href={getWidgetUrl(widget)} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Voir
                        </a>
                      </Button>
                    </div>
                  </div>
                  {/* Actions - horizontal on mobile, vertical on desktop */}
                  <div className="flex sm:flex-col items-center gap-1 border-t sm:border-t-0 sm:border-l pt-3 sm:pt-0 sm:pl-3">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-primary h-8 w-8"
                      onClick={() => navigate(`/company/widgets/${widget.id}`)}
                      title="Personnaliser"
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDuplicate(widget)} title="Dupliquer">
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive h-8 w-8" onClick={() => handleDelete(widget.id)} title="Supprimer">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            ))}
          </>
        )}
        </div>
      </div>

      {/* Widgets de démonstration (repliable) */}
      {widgets.filter(w => w.is_demo || w.is_default).length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <CardTitle className="text-sm font-medium text-amber-800">
                  Widgets démo ({widgets.filter(w => w.is_demo || w.is_default).length})
                </CardTitle>
              </div>
              <p className="text-xs text-amber-700">
                Exemples pré-configurés
              </p>
            </div>
          </CardHeader>
          <CardContent className="pt-2 px-3 sm:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {widgets.filter(w => w.is_demo || w.is_default).map((widget) => (
                <div 
                  key={widget.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-white/50"
                >
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${widget.primary_color || '#2F5233'}20` }}
                  >
                    {(() => {
                      const IconComponent = getWidgetIcon(widget.widget_type);
                      return <IconComponent className="w-4 h-4" style={{ color: widget.primary_color || '#2F5233' }} />;
                    })()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{widget.name}</p>
                    <Badge 
                      className="text-xs"
                      style={getWidgetBadgeStyle(widget.widget_type)}
                    >
                      {getWidgetLabel(widget.widget_type)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => copyIframeCode(widget)} title="Copier iframe">
                      <Code className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" asChild>
                      <a href={getWidgetUrl(widget)} target="_blank" rel="noopener noreferrer" title="Voir le widget">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// WidgetEditor moved to separate file

// Settings Page - Complete company settings
function SettingsPage() {
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    name: '', phone: '', address: '', logo_url: '', default_hourly_rate: '',
    cancellation_delay_hours: '', google_review_link: '', description: '', website: '', siret: '',
    contact_email: '',  // Email de contact de l'entreprise (distinct de l'email de connexion)
    primary_color: '#2F5233',
    smtp_host: '', smtp_port: '', smtp_user: '', smtp_password: '', smtp_sender_email: '', smtp_sender_name: '', smtp_enabled: false,
    stripe_enabled: false, stripe_secret_key: '', stripe_public_key: '',
    legal_documents: { cgv_url: '', privacy_url: '', mentions_url: '' },
    // Client settings
    require_client_approval: true,
    require_intervention_validation: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [searchParams] = useSearchParams();
  const [bookingModeSummary, setBookingModeSummary] = useState(null);
  const [savingBookingMode, setSavingBookingMode] = useState(false);

  // Load the current booking-mode summary across all widgets so the toggle
  // reflects reality on mount and stays in sync after each flip.
  const fetchBookingModeSummary = async () => {
    try {
      const res = await companyApi.getWidgetsBookingMode();
      setBookingModeSummary(res.data);
    } catch (_e) { /* silent, toggle just stays disabled */ }
  };

  useEffect(() => {
    fetchBookingModeSummary();
  }, []);

  const handleToggleAutoValidation = async (checked) => {
    setSavingBookingMode(true);
    try {
      await companyApi.setWidgetsBookingMode(checked ? 'direct' : 'approval');
      await fetchBookingModeSummary();
      toast.success(
        checked
          ? 'Validation automatique activée sur tous vos widgets'
          : 'Validation manuelle activée sur tous vos widgets',
      );
    } catch (_e) {
      toast.error('Impossible de mettre à jour le mode de validation');
    } finally {
      setSavingBookingMode(false);
    }
  };

  // Auto-select tab when redirected from banner CTA (?tab=smtp)
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && ['general', 'company', 'client-space', 'pricing', 'legal', 'smtp', 'templates', 'payments'].includes(t)) {
      setActiveTab(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // SMTP test panel state
  const [smtpTestRecipient, setSmtpTestRecipient] = useState('');
  const [isTestingSmtpConnection, setIsTestingSmtpConnection] = useState(false);
  const [isSendingSmtpTest, setIsSendingSmtpTest] = useState(false);
  const [smtpTestHistory, setSmtpTestHistory] = useState([]);

  // Email history (auto-purged after 15 days)
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsTotal, setEmailLogsTotal] = useState(0);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [emailLogsFilter, setEmailLogsFilter] = useState('all'); // all | success | failed
  const [emailLogsSearch, setEmailLogsSearch] = useState('');
  const [emailLogDetail, setEmailLogDetail] = useState(null); // currently opened log
  const [emailLogDetailLoading, setEmailLogDetailLoading] = useState(false);
  const [isClearingEmailLogs, setIsClearingEmailLogs] = useState(false);

  // ── SMTP wizard ──
  const [smtpWizardOpen, setSmtpWizardOpen] = useState(false);

  // ── Health status ──
  const [healthStatus, setHealthStatus] = useState(null);
  const [isRunningHealth, setIsRunningHealth] = useState(false);

  const refreshHealthStatus = async () => {
    try {
      const res = await companyApi.getHealthStatus();
      setHealthStatus(res.data);
    } catch (e) {
      // best-effort
    }
  };

  const runHealthChecksNow = async () => {
    setIsRunningHealth(true);
    try {
      await companyApi.runHealthChecksNow();
      await refreshHealthStatus();
      toast.success("Diagnostic terminé");
    } catch (e) {
      toast.error("Erreur lors du diagnostic");
    } finally {
      setIsRunningHealth(false);
    }
  };

  const refreshSmtpHistory = async () => {
    try {
      const res = await companyApi.getSmtpTestHistory();
      setSmtpTestHistory(res.data || []);
    } catch (e) {
      // best-effort; ignore
    }
  };

  const refreshEmailLogs = async () => {
    setEmailLogsLoading(true);
    try {
      const params = { limit: 100, skip: 0 };
      if (emailLogsFilter !== 'all') params.status_filter = emailLogsFilter;
      if (emailLogsSearch.trim()) params.search = emailLogsSearch.trim();
      const res = await companyApi.getEmailLogs(params);
      setEmailLogs(res.data?.items || []);
      setEmailLogsTotal(res.data?.total || 0);
    } catch (e) {
      // best-effort
    } finally {
      setEmailLogsLoading(false);
    }
  };

  const openEmailLogDetail = async (logId) => {
    setEmailLogDetailLoading(true);
    try {
      const res = await companyApi.getEmailLog(logId);
      setEmailLogDetail(res.data);
    } catch (e) {
      toast.error("Impossible de charger le contenu de l'email");
    } finally {
      setEmailLogDetailLoading(false);
    }
  };

  const clearAllEmailLogs = async () => {
    if (!window.confirm("Supprimer tout l'historique des emails ? Cette action est irréversible.")) return;
    setIsClearingEmailLogs(true);
    try {
      await companyApi.clearEmailLogs();
      toast.success("Historique vidé");
      refreshEmailLogs();
    } catch (e) {
      toast.error("Erreur lors du vidage");
    } finally {
      setIsClearingEmailLogs(false);
    }
  };

  useEffect(() => {
    refreshSmtpHistory();
    refreshEmailLogs();
    refreshHealthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Account settings state (email/password change)
  const [accountForm, setAccountForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
    new_email: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  
  // Email Templates state
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ template_type: '', subject: '', body: '' });
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  const fetchTemplates = async () => {
    try {
      const res = await companyApi.getEmailTemplates();
      setTemplates(res.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await companyApi.getProfile();
        const p = response.data;
        setProfile(p);
        setFormData({
          name: p.name || '', phone: p.phone || '', address: p.address || '',
          logo_url: p.logo_url || '', default_hourly_rate: p.default_hourly_rate?.toString() || '35',
          cancellation_delay_hours: p.cancellation_delay_hours?.toString() || '48',
          google_review_link: p.google_review_link || '', description: p.description || '',
          website: p.website || '', siret: p.siret || '',
          contact_email: p.contact_email || p.email || '',  // Use contact_email or fallback to email
          primary_color: p.primary_color || '#2F5233',
          smtp_host: p.smtp_host || '', smtp_port: p.smtp_port?.toString() || '587',
          smtp_user: p.smtp_user || '', smtp_password: p.smtp_password || '',
          smtp_sender_email: p.smtp_sender_email || '', smtp_sender_name: p.smtp_sender_name || '',
          smtp_enabled: p.smtp_enabled || false,
          stripe_enabled: p.stripe_enabled || false,
          stripe_secret_key: p.stripe_secret_key || '',
          stripe_public_key: p.stripe_public_key || '',
          legal_documents: p.legal_documents || { cgv_url: '', privacy_url: '', mentions_url: '' },
          // Client settings
          require_client_approval: p.require_client_approval !== false,  // default true
          require_intervention_validation: p.require_intervention_validation || false,
          // Email signature settings (separate from company info)
          email_signature_name: p.email_signature_name || '',
          email_signature_address: p.email_signature_address || '',
          email_signature_phone: p.email_signature_phone || '',
          email_signature_email: p.email_signature_email || '',
          email_signature_website: p.email_signature_website || '',
          email_signature_siret: p.email_signature_siret || '',
          use_custom_email_signature: p.use_custom_email_signature || false
        });
        await fetchTemplates();
      } catch (error) {
        toast.error('Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      template_type: template.template_type,
      subject: template.subject,
      body: template.body
    });
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.subject || !templateForm.body) {
      toast.error('Veuillez remplir le sujet et le contenu');
      return;
    }
    setIsSavingTemplate(true);
    try {
      await companyApi.saveEmailTemplate({
        template_type: templateForm.template_type,
        subject: templateForm.subject,
        body: templateForm.body,
        is_active: true
      });
      toast.success('Template sauvegardé');
      setEditingTemplate(null);
      await fetchTemplates();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleResetTemplate = async (templateId) => {
    try {
      await companyApi.deleteEmailTemplate(templateId);
      toast.success('Template réinitialisé');
      await fetchTemplates();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Clean up data - don't send empty strings for optional signature fields
      const cleanFormData = { ...formData };
      
      // Convert numeric fields
      cleanFormData.default_hourly_rate = parseFloat(formData.default_hourly_rate) || 35;
      cleanFormData.cancellation_delay_hours = parseInt(formData.cancellation_delay_hours) || 48;
      cleanFormData.smtp_port = parseInt(formData.smtp_port) || 587;
      
      // If custom signature is disabled, clear signature fields
      if (!cleanFormData.use_custom_email_signature) {
        delete cleanFormData.email_signature_name;
        delete cleanFormData.email_signature_address;
        delete cleanFormData.email_signature_phone;
        delete cleanFormData.email_signature_email;
        delete cleanFormData.email_signature_website;
        delete cleanFormData.email_signature_siret;
      }
      
      await companyApi.updateProfile(cleanFormData);
      // Update profile with new data so email preview reflects changes
      setProfile(prev => ({ ...prev, ...cleanFormData }));
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 5 Mo)');
      return;
    }

    setIsUploadingLogo(true);
    try {
      const res = await companyApi.uploadLogo(file);
      const newLogoUrl = res.data.logo_url;
      // Update both formData and profile to reflect the change immediately
      handleChange('logo_url', newLogoUrl);
      setProfile(prev => ({ ...prev, logo_url: newLogoUrl }));
      toast.success('Logo uploadé avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'upload du logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  if (isLoading) {
    return <div className="animate-pulse space-y-4">
      <div className="h-8 bg-muted rounded w-1/3" />
      <div className="h-96 bg-muted rounded-xl" />
    </div>;
  }

  // Construct full logo URL if relative - use /api/uploads for CORS
  const getLogoUrl = getAssetUrl;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Paramètres</h1>
          <p className="text-muted-foreground mt-1">Configurez votre entreprise</p>
        </div>
        <Button onClick={handleSave} disabled={isSaving} className="rounded-full" data-testid="save-settings-btn">
          {isSaving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap justify-start gap-1 h-auto bg-muted p-1 w-full">
          <TabsTrigger value="account" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Mon Compte</TabsTrigger>
          <TabsTrigger value="general" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Entreprise</TabsTrigger>
          <TabsTrigger value="clients" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Espace Client</TabsTrigger>
          <TabsTrigger value="tarification" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Tarification</TabsTrigger>
          <TabsTrigger value="legal" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Avis & Légal</TabsTrigger>
          <TabsTrigger value="smtp" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Emails</TabsTrigger>
          <TabsTrigger value="templates" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Templates</TabsTrigger>
          <TabsTrigger value="payments" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Paiements</TabsTrigger>
        </TabsList>

        {/* Account Tab - User Profile */}
        <TabsContent value="account" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <User className="w-5 h-5" />
                Mon profil utilisateur
              </CardTitle>
              <CardDescription>
                Vos identifiants de connexion à ResaWork
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-muted-foreground text-sm">Email de connexion</Label>
                    <p className="font-medium">{profile?.email || 'Non défini'}</p>
                  </div>
                  <Badge variant="outline">Compte administrateur</Badge>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Changer mon email de connexion</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Email actuel : <span className="font-medium text-foreground">{profile?.email}</span>
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nouvel email</Label>
                    <Input 
                      type="email"
                      value={accountForm.new_email}
                      onChange={(e) => setAccountForm({...accountForm, new_email: e.target.value})}
                      placeholder="nouveau@email.fr"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mot de passe actuel (confirmation)</Label>
                    <Input 
                      type="password"
                      value={accountForm.current_password}
                      onChange={(e) => setAccountForm({...accountForm, current_password: e.target.value})}
                      placeholder="••••••••"
                    />
                  </div>
                </div>
                <Button 
                  className="mt-3 rounded-full" 
                  variant="outline"
                  disabled={isChangingEmail || !accountForm.new_email || !accountForm.current_password}
                  onClick={async () => {
                    setIsChangingEmail(true);
                    try {
                      await companyApi.changeEmail({ 
                        new_email: accountForm.new_email, 
                        current_password: accountForm.current_password 
                      });
                      toast.success('Email modifié avec succès. Vous pouvez maintenant vous connecter avec votre nouvel email.');
                      setAccountForm({...accountForm, new_email: '', current_password: ''});
                      // Refresh profile
                      const res = await companyApi.getProfile();
                      setProfile(res.data);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Erreur lors de la modification de l\'email');
                    } finally {
                      setIsChangingEmail(false);
                    }
                  }}
                >
                  {isChangingEmail ? 'Modification...' : 'Modifier mon email'}
                </Button>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Changer mon mot de passe</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Mot de passe actuel</Label>
                    <Input 
                      type="password"
                      value={accountForm.current_password}
                      onChange={(e) => setAccountForm({...accountForm, current_password: e.target.value})}
                      placeholder="••••••••"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nouveau mot de passe</Label>
                    <Input 
                      type="password"
                      value={accountForm.new_password}
                      onChange={(e) => setAccountForm({...accountForm, new_password: e.target.value})}
                      placeholder="••••••••"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirmer</Label>
                    <Input 
                      type="password"
                      value={accountForm.confirm_password}
                      onChange={(e) => setAccountForm({...accountForm, confirm_password: e.target.value})}
                      placeholder="••••••••"
                    />
                  </div>
                </div>
                <Button 
                  className="mt-3 rounded-full" 
                  variant="outline"
                  disabled={isChangingPassword || !accountForm.current_password || !accountForm.new_password || accountForm.new_password !== accountForm.confirm_password}
                  onClick={async () => {
                    if (accountForm.new_password !== accountForm.confirm_password) {
                      toast.error('Les mots de passe ne correspondent pas');
                      return;
                    }
                    setIsChangingPassword(true);
                    try {
                      await companyApi.changePassword({
                        current_password: accountForm.current_password,
                        new_password: accountForm.new_password
                      });
                      toast.success('Mot de passe modifié');
                      setAccountForm({...accountForm, current_password: '', new_password: '', confirm_password: ''});
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Mot de passe actuel incorrect');
                    } finally {
                      setIsChangingPassword(false);
                    }
                  }}
                >
                  {isChangingPassword ? 'Modification...' : 'Modifier mon mot de passe'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-blue-50/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Différence entre profil et entreprise</p>
                  <ul className="space-y-1 text-blue-700">
                    <li>• <strong>Mon Compte</strong> : Vos identifiants personnels de connexion</li>
                    <li>• <strong>Entreprise</strong> : Coordonnées commerciales affichées aux clients (emails, widgets, factures)</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* General Tab - Company Info */}
        <TabsContent value="general" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Informations générales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom de l'entreprise *</Label>
                  <Input value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="Ma Société" data-testid="company-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>SIRET</Label>
                  <Input value={formData.siret} onChange={(e) => handleChange('siret', e.target.value)} placeholder="123 456 789 00012" />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="06 12 34 56 78" />
                </div>
                <div className="space-y-2">
                  <Label>Email de contact</Label>
                  <Input value={formData.contact_email || ''} onChange={(e) => handleChange('contact_email', e.target.value)} placeholder="contact@entreprise.fr" />
                  <p className="text-xs text-muted-foreground">Email affiché dans vos communications clients</p>
                </div>
                <div className="space-y-2">
                  <Label>Site web</Label>
                  <Input value={formData.website} onChange={(e) => handleChange('website', e.target.value)} placeholder="https://www.exemple.fr" />
                </div>
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input value={formData.address} onChange={(e) => handleChange('address', e.target.value)} placeholder="123 rue Exemple, 75001 Paris" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Description</Label>
                  <Textarea value={formData.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="Présentez votre entreprise..." rows={3} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Logo & Branding</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-6">
                {formData.logo_url ? (
                  <img src={getLogoUrl(formData.logo_url)} alt="Logo" className="w-24 h-24 object-cover border rounded-full bg-white" loading="lazy" decoding="async" />
                ) : (
                  <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center">
                    <Image className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 space-y-3">
                  <div>
                    <Label>Télécharger un logo</Label>
                    <div className="mt-2 flex items-center gap-3">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="logo-upload"
                        disabled={isUploadingLogo}
                      />
                      <label htmlFor="logo-upload">
                        <Button type="button" variant="outline" className="cursor-pointer" disabled={isUploadingLogo} asChild>
                          <span>
                            <Upload className="w-4 h-4 mr-2" />
                            {isUploadingLogo ? 'Upload...' : 'Choisir un fichier'}
                          </span>
                        </Button>
                      </label>
                      {formData.logo_url && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => handleChange('logo_url', '')}>
                          Supprimer
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">PNG, JPG, SVG ou WEBP. Max 5 Mo.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Avis Google</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Label>Lien Google My Business</Label>
              <Input value={formData.google_review_link} onChange={(e) => handleChange('google_review_link', e.target.value)} placeholder="https://g.page/r/..." />
              <p className="text-xs text-muted-foreground">Ce lien sera proposé à vos clients pour déposer un avis</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Personnalisation</CardTitle>
              <CardDescription>Personnalisez l'apparence de votre espace client</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Couleur principale</Label>
                <div className="flex gap-3 items-center">
                  <input
                    type="color"
                    value={formData.primary_color || '#2F5233'}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer border"
                  />
                  <Input 
                    value={formData.primary_color || '#2F5233'} 
                    onChange={(e) => handleChange('primary_color', e.target.value)} 
                    placeholder="#2F5233"
                    className="w-32"
                  />
                  <div 
                    className="px-4 py-2 rounded-full text-white text-sm font-medium"
                    style={{ backgroundColor: formData.primary_color || '#2F5233' }}
                  >
                    Aperçu bouton
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Cette couleur sera utilisée sur votre page de connexion client et vos emails</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Client Settings Tab */}
        <TabsContent value="clients" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Users className="w-5 h-5" />
                Paramètres Espace Client
              </CardTitle>
              <CardDescription>Configurez les options de l'espace client</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Client Account Approval */}
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-base font-medium">Validation manuelle des comptes clients</Label>
                    <p className="text-sm text-muted-foreground">
                      Lorsqu&apos;un client crée un compte, vous devez approuver sa demande avant qu&apos;il puisse accéder à son espace.
                    </p>
                  </div>
                  <Switch
                    checked={formData.require_client_approval}
                    onCheckedChange={(checked) => setFormData({...formData, require_client_approval: checked})}
                    data-testid="settings-require-client-approval-switch"
                  />
                </div>
                {formData.require_client_approval ? (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="settings-require-client-approval-on-banner">
                    <p className="text-sm text-amber-700">
                      <strong>Validation manuelle :</strong> Chaque nouveau compte client apparaîtra dans l&apos;onglet <em>Demandes</em> avec le statut « En attente ». Le client n&apos;accède à son espace qu&apos;une fois que vous l&apos;avez approuvé.
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="settings-require-client-approval-off-banner">
                    <p className="text-sm text-emerald-700">
                      <strong>Approbation automatique :</strong> Tout nouveau compte client est immédiatement actif — pas besoin de validation manuelle. Idéal pour les sites grand public où vous voulez réduire la friction d&apos;inscription.
                    </p>
                  </div>
                )}
              </div>

              {/* Booking auto-validation across all widgets */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-base font-medium">Validation automatique des réservations (tous les widgets)</Label>
                    <p className="text-sm text-muted-foreground">
                      Passez tous vos widgets publics en mode <strong>direct</strong> — chaque réservation client est immédiatement confirmée, sans validation manuelle depuis le back-office.
                    </p>
                    {bookingModeSummary && bookingModeSummary.total > 0 && (
                      <p className="text-xs text-muted-foreground pt-1">
                        {bookingModeSummary.direct}/{bookingModeSummary.total} widget(s) actuellement en confirmation automatique.
                      </p>
                    )}
                  </div>
                  <Switch
                    data-testid="settings-widgets-auto-validation-switch"
                    disabled={savingBookingMode || !bookingModeSummary || bookingModeSummary.total === 0}
                    checked={!!(bookingModeSummary && bookingModeSummary.all_direct)}
                    onCheckedChange={handleToggleAutoValidation}
                  />
                </div>
                {bookingModeSummary && bookingModeSummary.total === 0 ? (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-600">
                      Créez au moins un widget public pour activer ce réglage.
                    </p>
                  </div>
                ) : bookingModeSummary?.all_direct ? (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="settings-widgets-auto-validation-on-banner">
                    <p className="text-sm text-emerald-700">
                      <strong>Confirmation directe active :</strong> vos widgets acceptent instantanément chaque demande. Utile pour maximiser les conversions sur les créneaux disponibles.
                    </p>
                  </div>
                ) : bookingModeSummary?.all_approval ? (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="settings-widgets-auto-validation-off-banner">
                    <p className="text-sm text-amber-700">
                      <strong>Validation manuelle :</strong> chaque demande atterrit dans <em>Demandes</em>. Vous confirmez ou refusez avant qu&apos;elle ne passe dans le planning.
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="settings-widgets-auto-validation-mixed-banner">
                    <p className="text-sm text-blue-700">
                      <strong>Configuration mixte :</strong> certains widgets sont en mode direct, d&apos;autres en validation manuelle. Basculez ci-dessus pour tout uniformiser en un clic, ou éditez chaque widget individuellement pour un contrôle plus fin.
                    </p>
                  </div>
                )}
              </div>

              {/* Intervention Validation */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-base font-medium">Validation des prestations par le client</Label>
                    <p className="text-sm text-muted-foreground">
                      Après chaque intervention terminée, le client pourra confirmer que la prestation a été correctement réalisée.
                    </p>
                  </div>
                  <Switch
                    checked={formData.require_intervention_validation}
                    onCheckedChange={(checked) => setFormData({...formData, require_intervention_validation: checked})}
                  />
                </div>
                {formData.require_intervention_validation && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700">
                      <strong>Activé :</strong> Vos clients verront un bouton "Valider la prestation" dans leur espace après chaque intervention terminée. Ils pourront également signaler un problème si nécessaire.
                    </p>
                  </div>
                )}
              </div>

              <Button 
                className="w-full rounded-full mt-4" 
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Sauvegarder les paramètres
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Account Tab - Email/Password Change */}
        <TabsContent value="account" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <User className="w-5 h-5" />
                Mon compte
              </CardTitle>
              <CardDescription>Gérez vos identifiants de connexion</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Info */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Email actuel</p>
                <p className="font-medium">{profile?.email}</p>
              </div>

              {/* Change Email Section */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Changer d'email
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nouvel email</Label>
                    <Input 
                      type="email"
                      value={accountForm.new_email}
                      onChange={(e) => setAccountForm(f => ({ ...f, new_email: e.target.value }))}
                      placeholder="nouveau@email.com"
                      data-testid="new-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mot de passe actuel (confirmation)</Label>
                    <Input 
                      type="password"
                      value={accountForm.current_password}
                      onChange={(e) => setAccountForm(f => ({ ...f, current_password: e.target.value }))}
                      placeholder="••••••••"
                      data-testid="current-password-email"
                    />
                  </div>
                </div>
                <Button 
                  onClick={async () => {
                    if (!accountForm.new_email || !accountForm.current_password) {
                      toast.error('Veuillez remplir tous les champs');
                      return;
                    }
                    setIsChangingEmail(true);
                    try {
                      await companyApi.changeEmail({
                        new_email: accountForm.new_email,
                        current_password: accountForm.current_password
                      });
                      toast.success('Email modifié avec succès !');
                      setAccountForm(f => ({ ...f, new_email: '', current_password: '' }));
                      // Refresh profile
                      const response = await companyApi.getProfile();
                      setProfile(response.data);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Erreur lors du changement d\'email');
                    } finally {
                      setIsChangingEmail(false);
                    }
                  }}
                  disabled={isChangingEmail}
                  data-testid="change-email-btn"
                >
                  {isChangingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Changer l'email
                </Button>
              </div>

              {/* Change Password Section */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Changer de mot de passe
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Mot de passe actuel</Label>
                    <Input 
                      type="password"
                      value={accountForm.current_password}
                      onChange={(e) => setAccountForm(f => ({ ...f, current_password: e.target.value }))}
                      placeholder="••••••••"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nouveau mot de passe</Label>
                    <Input 
                      type="password"
                      value={accountForm.new_password}
                      onChange={(e) => setAccountForm(f => ({ ...f, new_password: e.target.value }))}
                      placeholder="••••••••"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirmer le nouveau mot de passe</Label>
                    <Input 
                      type="password"
                      value={accountForm.confirm_password}
                      onChange={(e) => setAccountForm(f => ({ ...f, confirm_password: e.target.value }))}
                      placeholder="••••••••"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                <Button 
                  onClick={async () => {
                    if (!accountForm.current_password || !accountForm.new_password || !accountForm.confirm_password) {
                      toast.error('Veuillez remplir tous les champs');
                      return;
                    }
                    if (accountForm.new_password !== accountForm.confirm_password) {
                      toast.error('Les mots de passe ne correspondent pas');
                      return;
                    }
                    if (accountForm.new_password.length < 8) {
                      toast.error('Le mot de passe doit contenir au moins 8 caractères');
                      return;
                    }
                    setIsChangingPassword(true);
                    try {
                      await companyApi.changePassword({
                        current_password: accountForm.current_password,
                        new_password: accountForm.new_password
                      });
                      toast.success('Mot de passe modifié avec succès !');
                      setAccountForm({ current_password: '', new_password: '', confirm_password: '', new_email: '' });
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
                    } finally {
                      setIsChangingPassword(false);
                    }
                  }}
                  disabled={isChangingPassword}
                  data-testid="change-password-btn"
                >
                  {isChangingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                  Changer le mot de passe
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tarification Tab */}
        <TabsContent value="tarification" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Euro className="w-5 h-5" />
                Tarif horaire
              </CardTitle>
              <CardDescription>Configuration optionnelle pour les estimations de prix</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Info banner */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">À quoi sert le tarif horaire ?</p>
                    <p className="text-blue-700">
                      Ce tarif est <strong>optionnel</strong> et sert uniquement à calculer des estimations de prix 
                      dans vos widgets de réservation (demandes ponctuelles et abonnements). 
                      Il n'est pas obligatoire si vous préférez définir vos prix manuellement pour chaque service.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prix par heure (€) <span className="text-muted-foreground text-xs">(optionnel)</span></Label>
                  <Input type="number" min="1" step="0.5" value={formData.default_hourly_rate} onChange={(e) => handleChange('default_hourly_rate', e.target.value)} placeholder="35" data-testid="hourly-rate-input" />
                </div>
                <div className="space-y-2">
                  <Label>Délai d'annulation (heures)</Label>
                  <Input type="number" min="0" value={formData.cancellation_delay_hours} onChange={(e) => handleChange('cancellation_delay_hours', e.target.value)} placeholder="48" />
                </div>
              </div>
              
              <div className="p-4 bg-muted/50 rounded-xl">
                <p className="text-sm font-medium mb-2">Exemple de calcul automatique</p>
                <p className="text-sm text-muted-foreground">
                  Pour 12 passages/an en demi-journée (4h) à {formData.default_hourly_rate || 35}€/h :
                </p>
                <p className="text-lg font-semibold mt-1">
                  Total annuel : {(12 * 4 * (parseFloat(formData.default_hourly_rate) || 35)).toFixed(2)}€
                </p>
                <div className="flex gap-4 mt-2">
                  <p className="text-sm">Part client (50%) : <span className="font-semibold text-primary">{((12 * 4 * (parseFloat(formData.default_hourly_rate) || 35)) / 2).toFixed(2)}€</span></p>
                  <p className="text-sm">Part URSSAF (50%) : <span className="font-semibold text-green-600">{((12 * 4 * (parseFloat(formData.default_hourly_rate) || 35)) / 2).toFixed(2)}€</span></p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Avis & Légal Tab */}
        <TabsContent value="legal" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Star className="w-5 h-5" />
                Avis Google
              </CardTitle>
              <CardDescription>Invitez vos clients à laisser un avis après chaque intervention</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Lien Google Places / Avis Google</Label>
                <Input 
                  value={formData.google_review_link} 
                  onChange={(e) => handleChange('google_review_link', e.target.value)} 
                  placeholder="https://g.page/r/..." 
                />
                <p className="text-xs text-muted-foreground">
                  Ce lien sera envoyé aux clients après chaque intervention terminée pour les inviter à déposer un avis.
                </p>
              </div>
              {formData.google_review_link && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Lien configuré - Les clients recevront une invitation après chaque prestation
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documents légaux
              </CardTitle>
              <CardDescription>Ajoutez les liens vers vos documents légaux (affichés sur votre widget de réservation)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Conditions Générales de Vente (CGV)</Label>
                <Input 
                  value={formData.legal_documents?.cgv_url || ''} 
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    legal_documents: { ...prev.legal_documents, cgv_url: e.target.value }
                  }))} 
                  placeholder="https://www.votresite.fr/cgv" 
                />
              </div>
              <div className="space-y-2">
                <Label>Politique de confidentialité</Label>
                <Input 
                  value={formData.legal_documents?.privacy_url || ''} 
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    legal_documents: { ...prev.legal_documents, privacy_url: e.target.value }
                  }))} 
                  placeholder="https://www.votresite.fr/confidentialite" 
                />
              </div>
              <div className="space-y-2">
                <Label>Mentions légales</Label>
                <Input 
                  value={formData.legal_documents?.mentions_url || ''} 
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    legal_documents: { ...prev.legal_documents, mentions_url: e.target.value }
                  }))} 
                  placeholder="https://www.votresite.fr/mentions-legales" 
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Ces liens seront affichés en pied de page de votre widget de réservation pour informer vos clients.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMTP Tab */}
        <TabsContent value="smtp" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Configuration SMTP
              </CardTitle>
              <CardDescription>Configurez l'envoi d'emails avec votre propre serveur SMTP</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🧙‍♂️</span>
                  <div>
                    <p className="font-medium text-sm text-indigo-900">Assistant de configuration</p>
                    <p className="text-xs text-indigo-700">Gmail / OVH / PlanetHoster / Outlook — guidé pas à pas</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={() => setSmtpWizardOpen(true)}
                  data-testid="smtp-wizard-open-btn"
                >
                  Lancer l'assistant
                </Button>
              </div>
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                <div>
                  <p className="font-medium">Activer l'envoi d'emails</p>
                  <p className="text-sm text-muted-foreground">Les notifications seront envoyées depuis votre adresse</p>
                </div>
                <Switch checked={formData.smtp_enabled} onCheckedChange={(v) => handleChange('smtp_enabled', v)} />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Serveur SMTP</Label>
                  <Input value={formData.smtp_host} onChange={(e) => handleChange('smtp_host', e.target.value)} placeholder="mail.planethoster.com" disabled={!formData.smtp_enabled} />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input type="number" value={formData.smtp_port} onChange={(e) => handleChange('smtp_port', e.target.value)} placeholder="587" disabled={!formData.smtp_enabled} />
                </div>
                <div className="space-y-2">
                  <Label>Utilisateur</Label>
                  <Input value={formData.smtp_user} onChange={(e) => handleChange('smtp_user', e.target.value)} placeholder="contact@votredomaine.fr" disabled={!formData.smtp_enabled} />
                </div>
                <div className="space-y-2">
                  <Label>Mot de passe</Label>
                  <Input type="password" value={formData.smtp_password} onChange={(e) => handleChange('smtp_password', e.target.value)} placeholder="••••••••" disabled={!formData.smtp_enabled} />
                </div>
                <div className="space-y-2">
                  <Label>Email expéditeur</Label>
                  <Input value={formData.smtp_sender_email} onChange={(e) => handleChange('smtp_sender_email', e.target.value)} placeholder="noreply@votredomaine.fr" disabled={!formData.smtp_enabled} />
                </div>
                <div className="space-y-2">
                  <Label>Nom expéditeur</Label>
                  <Input value={formData.smtp_sender_name} onChange={(e) => handleChange('smtp_sender_name', e.target.value)} placeholder="Mon Entreprise" disabled={!formData.smtp_enabled} />
                </div>
              </div>
              
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-sm text-blue-800">
                  <strong>PlanetHoster :</strong> Serveur: mail.planethoster.com | Port: 587 (STARTTLS) ou 465 (SSL)
                </p>
              </div>

              {/* SMTP Test Buttons */}
              {formData.smtp_enabled && formData.smtp_host && (
                <div className="pt-4 border-t space-y-3">
                  <div>
                    <Label htmlFor="smtp-test-recipient" className="text-sm">Destinataire du test (optionnel)</Label>
                    <Input
                      id="smtp-test-recipient"
                      type="email"
                      placeholder={formData.email || 'votre@email.com'}
                      value={smtpTestRecipient}
                      onChange={(e) => setSmtpTestRecipient(e.target.value)}
                      data-testid="smtp-test-recipient"
                      className="mt-1.5"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Laissez vide pour envoyer sur {formData.email || "l'email du compte"}.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="smtp-test-connection-btn"
                      onClick={async () => {
                        try {
                          setIsTestingSmtpConnection(true);
                          const response = await companyApi.testSmtpConnection();
                          if (response.data.success) {
                            toast.success('Connexion SMTP réussie !');
                          } else {
                            toast.error(`Échec: ${response.data.error}`);
                          }
                          refreshSmtpHistory();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Erreur de test');
                        } finally {
                          setIsTestingSmtpConnection(false);
                        }
                      }}
                      disabled={isTestingSmtpConnection || isSendingSmtpTest}
                    >
                      {isTestingSmtpConnection ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
                      Tester la connexion
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="smtp-send-test-btn"
                      onClick={async () => {
                        try {
                          setIsSendingSmtpTest(true);
                          const response = await companyApi.sendTestEmail((smtpTestRecipient || '').trim());
                          if (response.data.success) {
                            toast.success('Email de test envoyé ! Vérifiez votre boîte de réception.');
                          } else {
                            toast.error(`Échec: ${response.data.error}`);
                          }
                          refreshSmtpHistory();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || "Erreur d'envoi");
                        } finally {
                          setIsSendingSmtpTest(false);
                        }
                      }}
                      disabled={isSendingSmtpTest || isTestingSmtpConnection}
                    >
                      {isSendingSmtpTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Envoyer un email test
                    </Button>
                  </div>

                  {/* SMTP test history (10 most recent attempts) */}
                  {smtpTestHistory.length > 0 && (
                    <div className="mt-4 rounded-xl border bg-white" data-testid="smtp-test-history">
                      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-slate-50">
                        <p className="text-sm font-medium text-slate-700">Historique des tests</p>
                        <button
                          type="button"
                          onClick={refreshSmtpHistory}
                          className="text-xs text-slate-500 hover:text-slate-800"
                        >
                          Actualiser
                        </button>
                      </div>
                      <ul className="divide-y max-h-72 overflow-y-auto">
                        {smtpTestHistory.map((log, idx) => {
                          const date = new Date(log.created_at);
                          const dateLabel = date.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
                          const typeLabel = log.type === 'connection' ? 'Connexion' : 'Envoi';
                          return (
                            <li key={log.id || idx} className="px-4 py-2.5 text-sm flex items-start gap-3" data-testid={`smtp-log-${idx}`}>
                              <div className="mt-0.5 shrink-0">
                                {log.success ? (
                                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                                    <Check className="w-3.5 h-3.5 text-emerald-700" />
                                  </div>
                                ) : (
                                  <div className="w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center">
                                    <XCircle className="w-3.5 h-3.5 text-rose-700" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-slate-800">{typeLabel}</span>
                                  {log.recipient && (
                                    <span className="text-xs text-slate-500 truncate">→ {log.recipient}</span>
                                  )}
                                  <span className="text-xs text-slate-400 ml-auto">{dateLabel}</span>
                                </div>
                                {log.error && (
                                  <p className="text-xs text-rose-700 mt-1 line-clamp-2">{log.error}</p>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* ─── EMAIL HISTORY (sent emails - auto-purged after 15 days) ─── Always visible */}
              <div className="mt-6 rounded-xl border bg-white" data-testid="email-history-panel">
                <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-700" />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Historique des emails envoyés</p>
                      <p className="text-[11px] text-slate-500">Conservé 15 jours puis purgé automatiquement · {emailLogsTotal} email{emailLogsTotal > 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={refreshEmailLogs}
                      className="text-xs px-2 py-1 rounded-md border text-slate-600 hover:bg-slate-100"
                      data-testid="email-logs-refresh-btn"
                    >
                      Actualiser
                    </button>
                    <button
                      type="button"
                      onClick={clearAllEmailLogs}
                      disabled={isClearingEmailLogs || emailLogs.length === 0}
                      className="text-xs px-2 py-1 rounded-md border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                      data-testid="email-logs-clear-btn"
                    >
                      Vider
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b bg-white">
                  <div className="flex items-center gap-1 rounded-md border bg-slate-50 p-0.5">
                    {[
                      { value: 'all', label: 'Tous' },
                      { value: 'success', label: 'Succès' },
                      { value: 'failed', label: 'Échecs' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { setEmailLogsFilter(opt.value); setTimeout(refreshEmailLogs, 0); }}
                        className={`text-xs px-2.5 py-1 rounded ${emailLogsFilter === opt.value ? 'bg-white shadow text-slate-900 font-medium' : 'text-slate-600 hover:text-slate-900'}`}
                        data-testid={`email-logs-filter-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <Input
                    type="text"
                    value={emailLogsSearch}
                    onChange={(e) => setEmailLogsSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); refreshEmailLogs(); } }}
                    placeholder="Rechercher destinataire / objet..."
                    className="h-8 text-xs flex-1 min-w-[180px]"
                    data-testid="email-logs-search-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs"
                    onClick={refreshEmailLogs}
                    data-testid="email-logs-search-btn"
                  >
                    Filtrer
                  </Button>
                </div>

                {emailLogsLoading ? (
                  <div className="px-4 py-8 text-center text-sm text-slate-500">Chargement...</div>
                ) : emailLogs.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-slate-500" data-testid="email-logs-empty">
                    Aucun email enregistré pour le moment.
                  </div>
                ) : (
                  <ul className="divide-y max-h-[420px] overflow-y-auto">
                    {emailLogs.map((log, idx) => {
                      const date = new Date(log.created_at);
                      const dateLabel = date.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                      return (
                        <li
                          key={log.id || idx}
                          className="px-4 py-2.5 text-sm flex items-start gap-3 hover:bg-slate-50 cursor-pointer"
                          onClick={() => openEmailLogDetail(log.id)}
                          data-testid={`email-log-row-${idx}`}
                        >
                          <div className="mt-0.5 shrink-0">
                            {log.success ? (
                              <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                                <Check className="w-3.5 h-3.5 text-emerald-700" />
                              </div>
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center">
                                <XCircle className="w-3.5 h-3.5 text-rose-700" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-slate-800 truncate">{log.subject || '(sans objet)'}</span>
                              <span className="text-xs text-slate-400 ml-auto whitespace-nowrap">{dateLabel}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500 flex-wrap">
                              <span>→ {log.to}</span>
                              {log.provider && (
                                <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wide">
                                  {log.provider === 'company' ? 'SMTP perso' : 'SMTP global'}
                                </span>
                              )}
                            </div>
                            {!log.success && log.error && (
                              <p className="text-xs text-rose-700 mt-1 line-clamp-2">{log.error}</p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMTP Wizard modal */}
        <SmtpWizard
          open={smtpWizardOpen}
          onOpenChange={setSmtpWizardOpen}
          currentFormData={formData}
          onApply={(values) => {
            setFormData((prev) => ({ ...prev, ...values }));
          }}
          onTestComplete={async () => {
            // Persist current formData first so the test uses the new credentials
            try {
              await handleSave();
            } catch (e) {
              // ignore — toast already shown
            }
          }}
        />

        {/* Email log detail modal */}
        <Dialog open={!!emailLogDetail} onOpenChange={(open) => { if (!open) setEmailLogDetail(null); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="email-log-detail-modal">
            <DialogHeader>
              <DialogTitle className="font-heading text-base">
                {emailLogDetail?.subject || 'Détail email'}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {emailLogDetail && (
                  <>
                    Vers <span className="font-medium text-slate-700">{emailLogDetail.to}</span>
                    {' · '}
                    {new Date(emailLogDetail.created_at).toLocaleString('fr-FR')}
                    {emailLogDetail.provider && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] uppercase">
                        {emailLogDetail.provider === 'company' ? 'SMTP perso' : 'SMTP global'}
                      </span>
                    )}
                    <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] uppercase ${emailLogDetail.success ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                      {emailLogDetail.success ? 'Succès' : 'Échec'}
                    </span>
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto flex-1 -mx-6 px-6">
              {emailLogDetailLoading ? (
                <div className="py-10 text-center text-sm text-slate-500">Chargement...</div>
              ) : emailLogDetail ? (
                <>
                  {!emailLogDetail.success && emailLogDetail.error && (
                    <div className="mb-3 p-3 rounded-md bg-rose-50 border border-rose-200">
                      <p className="text-xs font-medium text-rose-800 mb-1">Erreur d'envoi</p>
                      <p className="text-xs text-rose-700 whitespace-pre-wrap">{emailLogDetail.error}</p>
                    </div>
                  )}
                  <div className="rounded-lg border bg-white overflow-hidden">
                    <iframe
                      title="email-content"
                      srcDoc={emailLogDetail.html_body || '<p style="font-family:sans-serif;color:#888;padding:24px">(contenu vide)</p>'}
                      sandbox=""
                      className="w-full"
                      style={{ height: '60vh', border: 'none' }}
                      data-testid="email-log-iframe"
                    />
                  </div>
                </>
              ) : null}
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setEmailLogDetail(null)} data-testid="email-log-detail-close">Fermer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Templates d'emails</CardTitle>
              <CardDescription>Personnalisez vos notifications automatiques. Le logo et les coordonnées de votre entreprise sont automatiquement ajoutés.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Email Header/Footer Preview */}
              <div className="p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="w-4 h-4 text-primary" />
                  <p className="text-sm font-medium text-primary">Aperçu de vos emails</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                  {/* Email Header with Logo */}
                  <div className="p-4 border-b bg-gray-50 flex items-center gap-3">
                    {formData.logo_url ? (
                      <img 
                        src={getLogoUrl(formData.logo_url)} 
                        alt={formData.name} 
                        className="w-14 h-14 rounded-full object-cover border-2 border-primary/20 bg-white flex-shrink-0"
                        loading="lazy" decoding="async"
                      />
                    ) : (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Image className="w-5 h-5" />
                        <span className="text-sm italic">Logo non défini - Ajoutez-le dans l'onglet "Général"</span>
                      </div>
                    )}
                  </div>
                  {/* Email Body Preview */}
                  <div className="p-4">
                    <p className="text-sm text-muted-foreground italic">Contenu de l'email...</p>
                  </div>
                  {/* Email Signature - Business Card Style */}
                  <div className="p-4 border-t bg-gray-50">
                    <div className="border-l-4 border-primary pl-4">
                      <p className="font-semibold text-gray-800">
                        {formData.use_custom_email_signature && formData.email_signature_name 
                          ? formData.email_signature_name 
                          : (profile?.name || 'Nom de votre entreprise')}
                      </p>
                      {(formData.use_custom_email_signature ? formData.email_signature_address : profile?.address) && (
                        <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1">
                          <MapPin className="w-3.5 h-3.5 text-gray-400" />
                          {formData.use_custom_email_signature ? formData.email_signature_address : profile.address}
                        </p>
                      )}
                      {(formData.use_custom_email_signature ? formData.email_signature_phone : profile?.phone) && (
                        <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1">
                          <Phone className="w-3.5 h-3.5 text-gray-400" />
                          {formData.use_custom_email_signature ? formData.email_signature_phone : profile.phone}
                        </p>
                      )}
                      {(formData.use_custom_email_signature ? formData.email_signature_email : (profile?.contact_email || profile?.email)) && (
                        <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1">
                          <Mail className="w-3.5 h-3.5 text-gray-400" />
                          {formData.use_custom_email_signature ? formData.email_signature_email : (profile.contact_email || profile.email)}
                        </p>
                      )}
                      {(formData.use_custom_email_signature ? formData.email_signature_website : profile?.website) && (
                        <p className="text-sm text-primary flex items-center gap-1.5 mt-1">
                          <ExternalLink className="w-3.5 h-3.5" />
                          {formData.use_custom_email_signature ? formData.email_signature_website : profile.website}
                        </p>
                      )}
                      {(formData.use_custom_email_signature ? formData.email_signature_siret : profile?.siret) && (
                        <p className="text-xs text-gray-400 mt-2">
                          SIRET : {formData.use_custom_email_signature ? formData.email_signature_siret : profile.siret}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Custom Signature Configuration */}
                <div className="mt-4 p-4 border rounded-lg bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="font-medium">Personnaliser la signature email</span>
                    </div>
                    <Switch
                      checked={formData.use_custom_email_signature}
                      onCheckedChange={(checked) => {
                        setFormData({ ...formData, use_custom_email_signature: checked });
                        if (checked && !formData.email_signature_name) {
                          // Pre-fill with company data
                          setFormData(prev => ({
                            ...prev,
                            use_custom_email_signature: true,
                            email_signature_name: profile?.name || '',
                            email_signature_address: profile?.address || '',
                            email_signature_phone: profile?.phone || '',
                            email_signature_email: profile?.contact_email || profile?.email || '',
                            email_signature_website: profile?.website || '',
                            email_signature_siret: profile?.siret || ''
                          }));
                        }
                      }}
                    />
                  </div>
                  
                  {formData.use_custom_email_signature ? (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Ces informations seront utilisées dans la signature de vos emails, indépendamment des coordonnées de votre profil.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nom commercial</Label>
                          <Input
                            value={formData.email_signature_name}
                            onChange={(e) => setFormData({ ...formData, email_signature_name: e.target.value })}
                            placeholder="Nom affiché dans la signature"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email de contact</Label>
                          <Input
                            type="email"
                            value={formData.email_signature_email}
                            onChange={(e) => setFormData({ ...formData, email_signature_email: e.target.value })}
                            placeholder="contact@monentreprise.fr"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Téléphone</Label>
                          <Input
                            value={formData.email_signature_phone}
                            onChange={(e) => setFormData({ ...formData, email_signature_phone: e.target.value })}
                            placeholder="06 00 00 00 00"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Site web</Label>
                          <Input
                            value={formData.email_signature_website}
                            onChange={(e) => setFormData({ ...formData, email_signature_website: e.target.value })}
                            placeholder="https://www.monsite.fr"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label>Adresse</Label>
                          <Input
                            value={formData.email_signature_address}
                            onChange={(e) => setFormData({ ...formData, email_signature_address: e.target.value })}
                            placeholder="123 Rue Exemple, 75000 Paris"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>SIRET (optionnel)</Label>
                          <Input
                            value={formData.email_signature_siret}
                            onChange={(e) => setFormData({ ...formData, email_signature_siret: e.target.value })}
                            placeholder="123 456 789 00012"
                          />
                        </div>
                      </div>
                      <Button 
                        onClick={handleSave} 
                        disabled={isSaving}
                        className="w-full sm:w-auto"
                      >
                        {isSaving ? 'Enregistrement...' : 'Enregistrer la signature'}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      La signature utilise actuellement les coordonnées de votre entreprise (onglet "Entreprise").
                      Activez cette option pour définir des informations différentes.
                    </p>
                  )}
                </div>
              </div>

              {/* Available tags */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-2">Balises disponibles :</p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="secondary">{'{client_name}'}</Badge>
                  <Badge variant="secondary">{'{client_email}'}</Badge>
                  <Badge variant="secondary">{'{booking_date}'}</Badge>
                  <Badge variant="secondary">{'{service_name}'}</Badge>
                  <Badge variant="secondary">{'{slot_type}'}</Badge>
                  <Badge variant="secondary">{'{collaborator_name}'}</Badge>
                  <Badge variant="secondary">{'{company_name}'}</Badge>
                  <Badge variant="secondary">{'{invoice_amount}'}</Badge>
                  <Badge variant="secondary">{'{temp_password}'}</Badge>
                </div>
              </div>

              {/* Templates List */}
              {templates.length > 0 ? (
                <div className="space-y-3">
                  {(() => {
                    const TEMPLATE_LABELS = {
                      booking_confirmation: 'Confirmation de réservation',
                      booking_reminder: 'Rappel de rendez-vous',
                      welcome: 'Email de bienvenue',
                      invoice: 'Facture',
                      cancellation: 'Annulation',
                      quote: 'Devis',
                      review_request: 'Demande d\'avis',
                    };
                    // Body is stored as free-form text/HTML. If it contains
                    // no tags we convert newlines to <br> so lists rendered
                    // with "-" or numbering still read cleanly.
                    const bodyToHtml = (raw) => {
                      if (!raw) return '';
                      const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
                      if (looksLikeHtml) return raw;
                      return raw
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/\n/g, '<br/>');
                    };
                    return templates.map(template => {
                      const label = TEMPLATE_LABELS[template.template_type] || template.template_type.replace(/_/g, ' ');
                      return (
                    <div key={template.id} className="p-4 border rounded-lg hover:bg-muted/30 transition-colors" data-testid={`email-template-${template.template_type}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-primary" />
                          <span className="font-medium">{label}</span>
                          {template.is_default && (
                            <Badge variant="outline" className="text-xs">Par défaut</Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEditTemplate(template)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          {!template.is_default && (
                            <Button variant="ghost" size="sm" onClick={() => handleResetTemplate(template.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">Sujet : {template.subject}</p>

                      {/* Rendered preview — matches what the client actually receives */}
                      <div
                        className="bg-white rounded-lg border border-border/60 shadow-sm overflow-hidden text-sm"
                        data-testid={`email-template-preview-${template.template_type}`}
                      >
                        {/* Header logo */}
                        <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-3">
                          {formData.logo_url ? (
                            <img
                              src={getLogoUrl(formData.logo_url)}
                              alt={formData.name}
                              className="w-12 h-12 rounded-full object-cover border-2 border-primary/20 bg-white flex-shrink-0"
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <div className="flex items-center gap-2 text-gray-400">
                              <Image className="w-4 h-4" />
                              <span className="text-xs italic">Votre logo</span>
                            </div>
                          )}
                        </div>

                        {/* Body */}
                        <div
                          className="px-4 py-4 text-gray-800 leading-relaxed max-h-52 overflow-hidden relative"
                          dangerouslySetInnerHTML={{ __html: bodyToHtml(template.body) }}
                        />

                        {/* Signature — mimics the branded signature added on send */}
                        <div className="px-4 py-3 border-t bg-gray-50">
                          <div className="pl-3 border-l-4 border-primary">
                            <p className="font-semibold text-gray-800 text-sm">
                              {formData.name || 'Votre entreprise'}
                            </p>
                            {formData.address && (
                              <p className="text-xs text-gray-600 mt-0.5">{formData.address}</p>
                            )}
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                              {formData.phone && (
                                <span className="text-xs text-gray-600">{formData.phone}</span>
                              )}
                              {profile?.email && (
                                <span className="text-xs text-gray-600">{profile.email}</span>
                              )}
                            </div>
                            {formData.website && (
                              <p className="text-xs text-primary mt-0.5">{formData.website}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                      );
                    });
                  })()}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">Chargement des templates...</p>
              )}

              {/* Edit Template Dialog */}
              {editingTemplate && (
                <Dialog open={!!editingTemplate} onOpenChange={(open) => !open && setEditingTemplate(null)}>
                  <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Modifier le template</DialogTitle>
                      <DialogDescription>
                        {({
                          booking_confirmation: 'Confirmation de réservation',
                          booking_reminder: 'Rappel de rendez-vous',
                          welcome: 'Email de bienvenue',
                          invoice: 'Facture',
                          cancellation: 'Annulation',
                          quote: 'Devis',
                          review_request: 'Demande d\'avis',
                        })[editingTemplate.template_type] || editingTemplate.template_type.replace(/_/g, ' ')}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
                      {/* Editor Side */}
                      <div className="space-y-4">
                        <div>
                          <Label>Sujet de l'email</Label>
                          <Input
                            value={templateForm.subject}
                            onChange={e => setTemplateForm(f => ({ ...f, subject: e.target.value }))}
                            placeholder="Sujet..."
                          />
                        </div>
                        <div>
                          <Label>Contenu de l'email (corps uniquement)</Label>
                          <Textarea
                            value={templateForm.body}
                            onChange={e => setTemplateForm(f => ({ ...f, body: e.target.value }))}
                            placeholder="Contenu..."
                            rows={12}
                            className="font-mono text-sm"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Le logo et la signature sont ajoutés automatiquement
                          </p>
                        </div>
                      </div>
                      
                      {/* Preview Side */}
                      <div className="space-y-2">
                        <Label>Aperçu de l'email</Label>
                        <div className="bg-white rounded-lg shadow-sm border overflow-hidden text-sm">
                          {/* Header with Logo */}
                          <div className="p-3 border-b bg-gray-50 flex items-center gap-3">
                            {formData.logo_url ? (
                              <img 
                                src={getLogoUrl(formData.logo_url)} 
                                alt={formData.name} 
                                className="w-12 h-12 rounded-full object-cover border-2 border-primary/20 bg-white flex-shrink-0"
                                loading="lazy" decoding="async"
                              />
                            ) : (
                              <div className="flex items-center gap-2 text-gray-400">
                                <Image className="w-4 h-4" />
                                <span className="text-xs italic">Votre logo</span>
                              </div>
                            )}
                          </div>
                          {/* Body Preview */}
                          <div
                            className="p-3 min-h-[120px] text-gray-700 leading-relaxed"
                            dangerouslySetInnerHTML={{
                              __html: (() => {
                                const raw = templateForm.body || 'Contenu de l\'email...';
                                const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
                                if (looksLikeHtml) return raw;
                                return raw
                                  .replace(/&/g, '&amp;')
                                  .replace(/</g, '&lt;')
                                  .replace(/>/g, '&gt;')
                                  .replace(/\n/g, '<br/>');
                              })()
                            }}
                          />
                          {/* Signature - Business Card Style */}
                          <div className="p-3 border-t bg-gray-50">
                            <div className="border-l-3 border-primary pl-3" style={{ borderLeftWidth: '3px' }}>
                              <p className="font-semibold text-gray-800 text-sm">{formData.name || 'Entreprise'}</p>
                              {formData.address && (
                                <p className="text-xs text-gray-600 mt-0.5">{formData.address}</p>
                              )}
                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                                {formData.phone && (
                                  <span className="text-xs text-gray-600">{formData.phone}</span>
                                )}
                                {profile?.email && (
                                  <span className="text-xs text-gray-600">{profile.email}</span>
                                )}
                              </div>
                              {formData.website && (
                                <p className="text-xs text-primary mt-0.5">{formData.website}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2 border-t">
                      <Button variant="outline" onClick={() => setEditingTemplate(null)}>Annuler</Button>
                      <Button onClick={handleSaveTemplate} disabled={isSavingTemplate}>
                        {isSavingTemplate ? 'Sauvegarde...' : 'Sauvegarder'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab - Stripe Configuration */}
        <TabsContent value="payments" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Configuration Stripe
              </CardTitle>
              <CardDescription>
                Configurez Stripe pour recevoir les paiements de vos clients via les widgets
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Comment configurer Stripe ?</h4>
                <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
                  <li>Créez un compte sur <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="underline">stripe.com</a></li>
                  <li>Accédez à votre Dashboard Stripe → Développeurs → Clés API</li>
                  <li>Copiez votre "Clé secrète" (commence par sk_live_ ou sk_test_)</li>
                  <li>Collez-la ci-dessous</li>
                </ol>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <Label className="text-base">Activer les paiements Stripe</Label>
                    <p className="text-sm text-muted-foreground">Permettre aux clients de payer via vos widgets</p>
                  </div>
                  <Switch
                    checked={formData.stripe_enabled || false}
                    onCheckedChange={(checked) => setFormData({ ...formData, stripe_enabled: checked })}
                    data-testid="stripe-enabled-switch"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Clé secrète Stripe</Label>
                  <div className="flex gap-2">
                    <Input
                      type="password"
                      value={formData.stripe_secret_key || ''}
                      onChange={(e) => setFormData({ ...formData, stripe_secret_key: e.target.value })}
                      placeholder="sk_live_... ou sk_test_..."
                      disabled={!formData.stripe_enabled}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formData.stripe_secret_key?.startsWith('sk_test_') ? (
                      <span className="text-amber-600">Mode test activé - aucun paiement réel ne sera effectué</span>
                    ) : formData.stripe_secret_key?.startsWith('sk_live_') ? (
                      <span className="text-green-600">Mode production - les paiements sont réels</span>
                    ) : (
                      'Entrez votre clé secrète Stripe pour activer les paiements'
                    )}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Clé publique Stripe (pour le frontend)</Label>
                  <Input
                    type="text"
                    value={formData.stripe_public_key || ''}
                    onChange={(e) => setFormData({ ...formData, stripe_public_key: e.target.value })}
                    placeholder="pk_live_... ou pk_test_..."
                    disabled={!formData.stripe_enabled}
                  />
                </div>
              </div>

              {formData.stripe_enabled && formData.stripe_secret_key && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-medium text-green-800 mb-2">Stripe est configuré</h4>
                  <p className="text-sm text-green-700">
                    Les clients pourront payer directement via vos widgets de prise de RDV payants.
                    Les factures seront automatiquement générées.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Subscription Plan Card Component
function SubscriptionPlanCard({ planId, name, price, description, features, isCurrentPlan, isBestValue }) {
  const [isLoading, setIsLoading] = useState(false);
  
  const handleSubscribe = async () => {
    setIsLoading(true);
    try {
      const response = await companyApi.createCheckoutSession(planId);
      if (response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du paiement');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Card className={`relative ${isBestValue ? 'border-green-500 border-2' : ''}`}>
      {isBestValue && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-green-500">Meilleure offre</Badge>
        </div>
      )}
      <CardHeader className="text-center pb-2">
        <CardTitle className="text-xl">{name}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="text-center space-y-4">
        <div>
          <span className="text-4xl font-bold">{price}€</span>
          <span className="text-muted-foreground">/{planId === 'annual' ? 'an' : 'mois'}</span>
        </div>
        <ul className="text-sm text-left space-y-2">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
        <Button 
          onClick={handleSubscribe}
          disabled={isLoading || isCurrentPlan}
          className="w-full"
          variant={isCurrentPlan ? 'outline' : 'default'}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Chargement...
            </>
          ) : isCurrentPlan ? (
            'Plan actuel'
          ) : (
            'Souscrire'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

// Account Page - Company account, license, and ResaWork invoices
function AccountPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [license, setLicense] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState({ monthly: 49, annual: 490 });
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [formData, setFormData] = useState({
    name: '', phone: '', address: '', logo_url: '', description: '', website: '', siret: '', email: '', google_review_link: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  
  // Account security state
  const [accountForm, setAccountForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
    new_email: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, licenseRes, plansRes, invoicesRes] = await Promise.all([
          companyApi.getProfile(),
          companyApi.getLicense().catch(() => ({ data: null })),
          companyApi.getSubscriptionPlans().catch(() => ({ data: { plans: [] } })),
          companyApi.getInvoices().catch(() => ({ data: [] }))
        ]);
        const p = profileRes.data;
        setProfile(p);
        setLicense(licenseRes.data);
        setInvoices(invoicesRes.data || []);
        
        // Set dynamic prices from API
        if (plansRes.data?.plans) {
          const monthly = plansRes.data.plans.find(plan => plan.id === 'monthly');
          const annual = plansRes.data.plans.find(plan => plan.id === 'annual');
          setSubscriptionPlans({
            monthly: monthly?.price || 49,
            annual: annual?.price || 490
          });
        }
        
        setFormData({
          name: p.name || '', phone: p.phone || '', address: p.address || '',
          logo_url: p.logo_url || '', description: p.description || '',
          website: p.website || '', siret: p.siret || '', email: p.email || '',
          google_review_link: p.google_review_link || ''
        });
      } catch (error) {
        toast.error('Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const getLogoUrl = getAssetUrl;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploadingLogo(true);
    try {
      const res = await companyApi.uploadLogo(file);
      handleChange('logo_url', res.data.logo_url);
      toast.success('Logo uploadé');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await companyApi.updateProfile(formData);
      toast.success('Profil mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="animate-pulse space-y-6">
      <div className="h-8 bg-muted rounded w-1/3" />
      <div className="h-64 bg-muted rounded-xl" />
    </div>;
  }

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/company')}>
          <ChevronRight className="w-5 h-5 rotate-180" />
        </Button>
        <div>
          <h1 className="text-3xl font-heading font-semibold">Mon Compte</h1>
          <p className="text-muted-foreground">Gérez votre entreprise et votre abonnement ResaWork</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap justify-start gap-1 h-auto bg-muted p-1 w-full">
          <TabsTrigger value="profile" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Mon Entreprise</TabsTrigger>
          <TabsTrigger value="security" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Sécurité</TabsTrigger>
          <TabsTrigger value="license" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Ma Licence</TabsTrigger>
          <TabsTrigger value="subscription" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Abonnement</TabsTrigger>
          <TabsTrigger value="invoices" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Mes Factures</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informations de l'entreprise</CardTitle>
              <CardDescription>Ces informations sont visibles par vos clients</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo */}
              <div className="flex items-center gap-6">
                <div className="relative group">
                  {formData.logo_url ? (
                    <img src={getLogoUrl(formData.logo_url)} alt="Logo" className="w-24 h-24 object-cover border rounded-full bg-white" loading="lazy" decoding="async" />
                  ) : (
                    <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center">
                      <Image className="w-8 h-8 text-muted-foreground" />
                    </div>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    <Upload className="w-6 h-6 text-white" />
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={isUploadingLogo} />
                  </label>
                </div>
                <div>
                  <p className="font-medium">Logo de l'entreprise</p>
                  <p className="text-sm text-muted-foreground">Format recommandé : PNG ou SVG, 500x500px</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom de l'entreprise *</Label>
                  <Input value={formData.name} onChange={(e) => handleChange('name', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Site web</Label>
                  <Input value={formData.website} onChange={(e) => handleChange('website', e.target.value)} placeholder="https://..." />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Adresse</Label>
                  <Textarea value={formData.address} onChange={(e) => handleChange('address', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>SIRET</Label>
                  <Input value={formData.siret} onChange={(e) => handleChange('siret', e.target.value)} />
                </div>
              </div>

              <Button onClick={handleSave} disabled={isSaving} className="rounded-full">
                {isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab - Email/Password Change */}
        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Key className="w-5 h-5" />
                Sécurité du compte
              </CardTitle>
              <CardDescription>Gérez vos identifiants de connexion</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Info */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Email actuel</p>
                <p className="font-medium">{profile?.email}</p>
              </div>

              {/* Change Email Section */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Changer d'email
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nouvel email</Label>
                    <Input 
                      type="email"
                      value={accountForm.new_email}
                      onChange={(e) => setAccountForm(f => ({ ...f, new_email: e.target.value }))}
                      placeholder="nouveau@email.com"
                      data-testid="new-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mot de passe actuel (confirmation)</Label>
                    <Input 
                      type="password"
                      value={accountForm.current_password}
                      onChange={(e) => setAccountForm(f => ({ ...f, current_password: e.target.value }))}
                      placeholder="••••••••"
                      data-testid="current-password-email"
                    />
                  </div>
                </div>
                <Button 
                  onClick={async () => {
                    if (!accountForm.new_email || !accountForm.current_password) {
                      toast.error('Veuillez remplir tous les champs');
                      return;
                    }
                    setIsChangingEmail(true);
                    try {
                      await companyApi.changeEmail({
                        new_email: accountForm.new_email,
                        current_password: accountForm.current_password
                      });
                      toast.success('Email modifié avec succès !');
                      setAccountForm(f => ({ ...f, new_email: '', current_password: '' }));
                      const response = await companyApi.getProfile();
                      setProfile(response.data);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Erreur lors du changement d\'email');
                    } finally {
                      setIsChangingEmail(false);
                    }
                  }}
                  disabled={isChangingEmail}
                  data-testid="change-email-btn"
                >
                  {isChangingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Changer l'email
                </Button>
              </div>

              {/* Change Password Section */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Changer de mot de passe
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Mot de passe actuel</Label>
                    <Input 
                      type="password"
                      value={accountForm.current_password}
                      onChange={(e) => setAccountForm(f => ({ ...f, current_password: e.target.value }))}
                      placeholder="••••••••"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nouveau mot de passe</Label>
                    <Input 
                      type="password"
                      value={accountForm.new_password}
                      onChange={(e) => setAccountForm(f => ({ ...f, new_password: e.target.value }))}
                      placeholder="••••••••"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirmer le nouveau mot de passe</Label>
                    <Input 
                      type="password"
                      value={accountForm.confirm_password}
                      onChange={(e) => setAccountForm(f => ({ ...f, confirm_password: e.target.value }))}
                      placeholder="••••••••"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                <Button 
                  onClick={async () => {
                    if (!accountForm.current_password || !accountForm.new_password || !accountForm.confirm_password) {
                      toast.error('Veuillez remplir tous les champs');
                      return;
                    }
                    if (accountForm.new_password !== accountForm.confirm_password) {
                      toast.error('Les mots de passe ne correspondent pas');
                      return;
                    }
                    if (accountForm.new_password.length < 8) {
                      toast.error('Le mot de passe doit contenir au moins 8 caractères');
                      return;
                    }
                    setIsChangingPassword(true);
                    try {
                      await companyApi.changePassword({
                        current_password: accountForm.current_password,
                        new_password: accountForm.new_password
                      });
                      toast.success('Mot de passe modifié avec succès !');
                      setAccountForm({ current_password: '', new_password: '', confirm_password: '', new_email: '' });
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
                    } finally {
                      setIsChangingPassword(false);
                    }
                  }}
                  disabled={isChangingPassword}
                  data-testid="change-password-btn"
                >
                  {isChangingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                  Changer le mot de passe
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* License Tab */}
        <TabsContent value="license" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Ma Licence ResaWork</CardTitle>
              <CardDescription>Détails de votre abonnement</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {license ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-muted/50 rounded-xl">
                      <p className="text-sm text-muted-foreground">Statut</p>
                      <Badge variant={license.is_active ? 'default' : 'destructive'} className="mt-1">
                        {license.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-xl">
                      <p className="text-sm text-muted-foreground">Plan</p>
                      <p className="font-semibold capitalize mt-1">{license.plan || 'Standard'}</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-xl">
                      <p className="text-sm text-muted-foreground">Prix mensuel</p>
                      <p className="font-semibold mt-1">{license.monthly_price || 0}€</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-xl">
                      <p className="text-sm text-muted-foreground">Expiration</p>
                      <p className="font-semibold mt-1">
                        {license.expiration_date 
                          ? new Date(license.expiration_date).toLocaleDateString('fr-FR') 
                          : 'Illimitée'}
                      </p>
                    </div>
                  </div>

                  {license.is_trial && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-center gap-2 text-amber-800">
                        <Clock className="w-5 h-5" />
                        <p className="font-medium">Période d'essai</p>
                      </div>
                      <p className="text-sm text-amber-700 mt-1">
                        Votre essai gratuit expire le {new Date(license.trial_end_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  )}

                  {/* Discount/Promo Info */}
                  {(license.discount_percent > 0 || license.discount_reason) && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                      <div className="flex items-center gap-2 text-green-800">
                        <DollarSign className="w-5 h-5" />
                        <p className="font-medium">Offre promotionnelle active</p>
                      </div>
                      <div className="mt-2 space-y-1">
                        {license.discount_percent > 0 && (
                          <p className="text-sm text-green-700">
                            <span className="font-semibold">Réduction : {license.discount_percent}%</span>
                          </p>
                        )}
                        {license.discount_reason && (
                          <p className="text-sm text-green-700">
                            Motif : {license.discount_reason}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground mb-2">Clé de licence</p>
                    <code className="text-xs bg-muted px-3 py-2 rounded font-mono block">
                      {license.license_key || 'N/A'}
                    </code>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Aucune licence trouvée</p>
                  <Button className="mt-4 rounded-full" onClick={() => window.open('/onboarding', '_blank')}>
                    Obtenir une licence
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscription Tab - Stripe Integration */}
        <TabsContent value="subscription" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Abonnement ResaWork
              </CardTitle>
              <CardDescription>Gérez votre abonnement et votre facturation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current subscription status */}
              {profile?.subscription_status === 'active' ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center gap-2 text-green-800">
                    <CheckCircle2 className="w-5 h-5" />
                    <p className="font-medium">Abonnement actif</p>
                  </div>
                  <p className="text-sm text-green-700 mt-1">
                    Plan {profile?.subscription_plan === 'annual' ? 'Annuel' : 'Mensuel'} - 
                    Expire le {profile?.subscription_end_date ? new Date(profile.subscription_end_date).toLocaleDateString('fr-FR') : 'N/A'}
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-center gap-2 text-amber-800">
                    <Clock className="w-5 h-5" />
                    <p className="font-medium">Période d'essai</p>
                  </div>
                  <p className="text-sm text-amber-700 mt-1">
                    Choisissez un plan pour continuer à utiliser ResaWork après votre essai gratuit.
                  </p>
                </div>
              )}

              {/* Subscription Plans */}
              <div>
                <h3 className="font-semibold mb-4">Choisissez votre plan</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Monthly Plan */}
                  <SubscriptionPlanCard 
                    planId="monthly"
                    name="Mensuel"
                    price={subscriptionPlans.monthly}
                    description="Idéal pour démarrer"
                    features={['Toutes les fonctionnalités', 'Support par email', 'Widget personnalisable', 'Collaborateurs illimités']}
                    isCurrentPlan={profile?.subscription_plan === 'monthly'}
                  />
                  
                  {/* Annual Plan */}
                  <SubscriptionPlanCard 
                    planId="annual"
                    name="Annuel"
                    price={subscriptionPlans.annual}
                    description="2 mois offerts"
                    features={['Toutes les fonctionnalités', 'Support prioritaire', 'Widget personnalisable', 'Collaborateurs illimités', '2 mois gratuits']}
                    isCurrentPlan={profile?.subscription_plan === 'annual'}
                    isBestValue
                  />
                </div>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                Paiement sécurisé par Stripe. TVA incluse.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Mes Factures ResaWork</CardTitle>
              <CardDescription>Historique de vos factures d'abonnement</CardDescription>
            </CardHeader>
            <CardContent>
              {invoices && invoices.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N° Facture</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Montant TTC</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-mono">{invoice.invoice_number}</TableCell>
                        <TableCell>{new Date(invoice.created_at).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell className="font-medium">{invoice.amount_ttc?.toFixed(2)}€</TableCell>
                        <TableCell>
                          <Badge variant={invoice.payment_status === 'paid' ? 'default' : 'secondary'} 
                            className={invoice.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}>
                            {invoice.payment_status === 'paid' ? 'Payée' : 'En attente'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {invoice.pdf_url && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${invoice.pdf_url}`, '_blank')}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Télécharger
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune facture pour le moment</p>
                  <p className="text-sm mt-2">Vos factures apparaîtront ici après votre premier paiement</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Subscription Success Page
function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [attempts, setAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setMessage("Session de paiement non trouvée");
      return;
    }
    pollPaymentStatus();
  }, [sessionId]);
  
  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) {
      setStatus('error');
      setMessage("Vérification du paiement expirée. Veuillez vérifier votre email.");
      return;
    }
    
    try {
      const response = await companyApi.getSubscriptionStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setMessage(response.data.message || "Abonnement activé avec succès!");
        toast.success("Abonnement activé!");
        return;
      }
      if (response.data.status === 'expired') {
        setStatus('error');
        setMessage("Session de paiement expirée. Veuillez réessayer.");
        return;
      }
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    } catch (error) {
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };
  
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            {status === 'loading' && <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />}
            {status === 'success' && <Check className="w-12 h-12 text-green-500" />}
            {status === 'error' && <XCircle className="w-12 h-12 text-red-500" />}
          </div>
          <CardTitle>
            {status === 'loading' && "Vérification du paiement..."}
            {status === 'success' && "Paiement Confirmé!"}
            {status === 'error' && "Erreur de paiement"}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">{message || (status === 'loading' && "Veuillez patienter...")}</p>
          {status !== 'loading' && (
            <Button onClick={() => navigate('/company')} className="w-full">
              {status === 'success' ? 'Accéder à mon espace' : 'Retour au tableau de bord'}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Global health banner — visible on all company dashboard pages
function GlobalHealthBanner() {
  const [status, setStatus] = useState(null);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    let mounted = true;
    companyApi.getHealthStatus()
      .then((res) => { if (mounted) setStatus(res.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const runChecks = async () => {
    setIsRunning(true);
    try {
      await companyApi.runHealthChecksNow();
      const res = await companyApi.getHealthStatus();
      setStatus(res.data);
      toast.success('Diagnostic terminé');
    } catch (e) {
      toast.error('Erreur lors du diagnostic');
    } finally {
      setIsRunning(false);
    }
  };

  return <HealthAlertBanner status={status} onRunChecks={runChecks} isRunning={isRunning} />;
}

// Main Company Dashboard Layout
export default function CompanyDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({ logo_url: '', name: '', is_suspended: false });
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);
  const [companyWidgets, setCompanyWidgets] = useState([]);
  const [pendingClientAccounts, setPendingClientAccounts] = useState(0);

  // Lightweight poll of /api/company/stats to surface the count of client
  // accounts awaiting validation directly in the sidebar — without it the
  // user has to navigate to /company/clients to even notice there are any.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await companyApi.getStats();
        if (!cancelled) {
          setPendingClientAccounts(res.data?.pending_client_accounts || 0);
        }
      } catch (e) { /* silent */ }
    };
    refresh();
    const interval = setInterval(refresh, 60_000); // every minute is plenty
    return () => { cancelled = true; clearInterval(interval); };
  }, [location.pathname]);

  // Fetch company info for logo display and suspension status
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const res = await companyApi.getProfile();
        const isSuspended = res.data.is_suspended || false;
        setCompanyInfo({
          logo_url: res.data.logo_url || '',
          name: res.data.name || '',
          is_suspended: isSuspended,
          require_client_approval: res.data.require_client_approval !== false
        });
        if (isSuspended) {
          setShowSuspensionModal(true);
        }
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };
    
    const fetchWidgets = async () => {
      try {
        const res = await companyApi.getWidgets();
        setCompanyWidgets(res.data || []);
      } catch (error) {
        console.error('Error fetching widgets:', error);
      }
    };
    
    fetchCompanyInfo();
    fetchWidgets();
  }, []);

  // One-time onboarding toast for the "client approval" badge
  useEffect(() => {
    if (companyInfo?.require_client_approval === undefined) return;
    const SEEN_KEY = 'resawork_client_approval_badge_onboarded';
    if (localStorage.getItem(SEEN_KEY) === '1') return;
    const timer = setTimeout(() => {
      const isManual = companyInfo.require_client_approval;
      toast(
        isManual
          ? 'Validation des comptes clients : Manuelle'
          : 'Validation des comptes clients : Automatique',
        {
          description: 'Cliquez sur le badge en haut à droite pour modifier ce réglage à tout moment.',
          action: {
            label: 'Modifier',
            onClick: () => navigate('/company/settings'),
          },
          duration: 8000,
        }
      );
      localStorage.setItem(SEEN_KEY, '1');
    }, 2500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo?.require_client_approval]);

  const getCompanyLogoUrl = getAssetUrl;

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Déconnexion réussie');
  };

  // Suspension Modal
  const SuspensionModal = () => (
    <Dialog open={showSuspensionModal} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-destructive flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Compte suspendu
          </DialogTitle>
          <DialogDescription>
            Votre compte a été suspendu pour non-paiement de votre licence ResaWork.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Important :</strong> Votre widget de réservation reste actif pour vos clients. 
              Cependant, vous ne pouvez plus accéder aux fonctionnalités de gestion tant que votre licence n'est pas régularisée.
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            Pour régulariser votre situation, veuillez contacter notre équipe ou effectuer le paiement de votre licence.
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/company/account')} className="flex-1">
            Mon compte
          </Button>
          <Button onClick={() => window.location.href = 'mailto:support@resawork.com'} className="flex-1">
            Contacter le support
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Navigation organisée par sections avec conditions
  // Check which widget types are active
  const hasRecurringWidget = companyWidgets.some(w => (w.widget_type === 'recurring' || w.widget_type === 'subscription') && w.is_active !== false);
  const hasPunctualWidget = companyWidgets.some(w => w.widget_type === 'punctual' && w.is_active !== false);
  const hasAppointmentWidget = companyWidgets.some(w => w.widget_type === 'appointment' && w.is_active !== false);
  const hasAnyWidget = hasRecurringWidget || hasPunctualWidget || hasAppointmentWidget;

  // Items mis en avant - PRIORITÉ : Planning, Collaborateurs, Clients
  const pinnedItems = [
    { path: '/company', icon: LayoutDashboard, label: 'Tableau de bord', end: true },
    { path: '/company/planning', icon: Calendar, label: 'Planning', highlighted: true, primary: true },
    { path: '/company/collaborators', icon: User, label: 'Collaborateurs', highlighted: true, primary: true },
    { path: '/company/clients', icon: Users, label: 'Clients', highlighted: true, primary: true, pendingBadge: pendingClientAccounts },
  ];

  // Groupes déployables (configuration & réglages)
  const collapsibleGroups = [
    {
      key: 'activity',
      title: 'Activité',
      icon: Activity,
      items: [
        ...(hasAnyWidget ? [
          { path: '/company/requests', icon: ShoppingCart, label: 'Demandes' },
        ] : []),
        ...(hasAppointmentWidget ? [
          { path: '/company/rdv-planning', icon: Video, label: 'Planning RDV' },
        ] : []),
        { path: '/company/interventions', icon: CheckCircle2, label: 'Prestations terminées' },
        { path: '/company/reviews', icon: Star, label: 'Avis Clients' },
        { path: '/company/trash', icon: Trash2, label: 'Corbeille' },
      ]
    },
    {
      key: 'business',
      title: 'Mon entreprise',
      icon: Briefcase,
      items: [
        { path: '/company/services', icon: Briefcase, label: 'Services' },
        { path: '/company/slot-types', icon: Clock, label: 'Types de créneaux' },
        { path: '/company/work-hours', icon: Clock, label: 'Horaires de travail' },
        { path: '/company/closures', icon: Calendar, label: 'Fermetures' },
        { path: '/company/leave-requests', icon: Clock, label: 'Demandes Absences', badge: true },
        { path: '/company/leave-history', icon: FileText, label: 'Historique Absences' },
      ]
    },
    {
      key: 'finance',
      title: 'Finances',
      icon: TrendingUp,
      items: [
        { path: '/company/mrr', icon: TrendingUp, label: 'MRR / Revenus' },
        { path: '/company/payments', icon: CreditCard, label: 'Paiements' },
      ]
    },
    {
      key: 'widgets',
      title: 'Widgets',
      icon: Layers,
      items: [
        { path: '/company/widgets', icon: Layers, label: 'Mes Widgets' },
      ]
    },
  ];

  // Auto-ouvrir le groupe contenant la route active.
  // L'utilisateur a explicitement demandé que "Activité" reste ouvert par défaut — c'est le bloc le plus important.
  const initialOpenGroups = {};
  collapsibleGroups.forEach(g => {
    initialOpenGroups[g.key] = g.key === 'activity' || g.items.some(it => location.pathname.startsWith(it.path));
  });
  const [openGroups, setOpenGroups] = useState(initialOpenGroups);
  const toggleGroup = (key) => setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));

  // Flatten pour menu mobile
  const navItems = [
    ...pinnedItems,
    ...collapsibleGroups.flatMap(g => g.items),
  ];

  // Helper class pour items NavLink
  const getNavItemClass = (item, isActive, mobile = false) => {
    const padding = mobile ? 'px-3 py-2.5' : 'px-3 py-2';
    if (mobile) {
      // Mobile keeps the legacy light style
      if (item.primary) {
        return `flex items-center gap-3 ${padding} rounded-lg transition-colors ${
          isActive
            ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
            : 'bg-primary/5 text-foreground hover:bg-primary/10 font-medium'
        }`;
      }
      return `flex items-center gap-3 ${padding} rounded-lg transition-colors ${
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`;
    }
    // Desktop = dark "command bar"
    if (item.primary) {
      return `flex items-center gap-3 ${padding} rounded-lg transition-all ${
        isActive
          ? 'bg-primary text-primary-foreground font-semibold shadow-[0_4px_12px_-4px_hsl(var(--primary)/0.6)]'
          : 'text-slate-200 hover:bg-white/[0.06] hover:text-white font-medium'
      }`;
    }
    return `flex items-center gap-3 ${padding} rounded-lg text-[13px] transition-colors ${
      isActive
        ? 'bg-white/[0.08] text-white font-medium border-l-2 border-primary pl-[10px]'
        : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-100'
    }`;
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Announcement Banner - Fixed at top */}
      <AnnouncementBanner />
      
      <div className="flex-1 flex flex-col lg:flex-row">
      {/* Suspension Modal */}
      <SuspensionModal />
      
      {/* Mobile Header */}
      <header className="lg:hidden flex items-center justify-between p-4 bg-card border-b border-border sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <img 
            src="/logo.png" 
            alt="ResaWork" 
            className="h-7"
          />
        </div>
        <div className="flex items-center gap-2">
          {/* Client approval mode indicator - mobile (compact) */}
          {companyInfo?.require_client_approval !== undefined && (
            <button
              type="button"
              onClick={() => navigate('/company/settings')}
              data-testid="client-approval-mode-indicator-mobile"
              title={`Validation clients : ${companyInfo.require_client_approval ? 'Manuelle' : 'Automatique'}`}
              className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors ${
                companyInfo.require_client_approval
                  ? 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
                  : 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
              }`}
            >
              {companyInfo.require_client_approval ? (
                <ClipboardList className="w-4 h-4" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
            </button>
          )}
          {/* Push Notifications Toggle */}
          <PushNotificationToggle className="hidden sm:flex" />
          {/* Notifications */}
          <NotificationBell />
          {/* Company logo - mobile */}
          <button 
            onClick={() => navigate('/company/settings')}
            className="flex items-center gap-2 p-1 rounded-lg hover:bg-muted transition-colors"
          >
            {companyInfo.logo_url ? (
              <img 
                src={getCompanyLogoUrl(companyInfo.logo_url)} 
                alt={companyInfo.name} 
                className="w-8 h-8 rounded-full object-cover border" loading="lazy" decoding="async"
              />
            ) : (
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                <span className="text-xs font-medium text-primary">{companyInfo.name?.charAt(0) || 'E'}</span>
              </div>
            )}
          </button>
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-testid="mobile-menu-btn">
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[57px] bg-background/95 backdrop-blur-sm z-40 overflow-y-auto">
          <nav className="p-4 space-y-1">
            {/* Items épinglés */}
            {pinnedItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) => getNavItemClass(item, isActive, true)}
              >
                <item.icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                {item.pendingBadge > 0 && (
                  <span
                    className="ml-auto inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-500 text-white min-w-[20px]"
                    data-testid={`nav-pending-${item.path.split('/').pop()}`}
                    title={`${item.pendingBadge} en attente`}
                  >
                    {item.pendingBadge}
                  </span>
                )}
              </NavLink>
            ))}

            {/* Séparateur */}
            <div className="pt-3 mt-3 border-t border-border">
              <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Configuration
              </p>
            </div>

            {/* Groupes déployables */}
            {collapsibleGroups.map((group) => (
              <div key={group.key} className="space-y-1">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.key)}
                  data-testid={`mobile-group-toggle-${group.key}`}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <group.icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{group.title}</span>
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${openGroups[group.key] ? 'rotate-180' : ''}`} />
                </button>
                {openGroups[group.key] && (
                  <div className="ml-4 pl-3 border-l border-border space-y-1">
                    {group.items.map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.end}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={({ isActive }) => getNavItemClass(item, isActive, true)}
                      >
                        <item.icon className="w-4 h-4" />
                        <span className="text-sm">{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-4 mt-4 border-t border-border">
              <button 
                onClick={() => { navigate('/company/settings'); setIsMobileMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 w-full text-left hover:bg-muted rounded-xl transition-colors"
              >
                {companyInfo.logo_url ? (
                  <img 
                    src={getCompanyLogoUrl(companyInfo.logo_url)} 
                    alt={companyInfo.name} 
                    className="w-10 h-10 rounded-full object-cover border" loading="lazy" decoding="async"
                  />
                ) : (
                  <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium">{companyInfo.name?.charAt(0) || 'E'}</span>
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium">{companyInfo.name || 'Mon Entreprise'}</p>
                  <p className="text-xs text-muted-foreground">Mon compte & Licence</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-muted-foreground hover:text-destructive px-4 mt-2"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
              <VersionBadge />
            </div>
          </nav>
        </div>
      )}

      {/* Desktop Sidebar - Dark "Command Bar" */}
      <aside className="hidden lg:flex w-64 flex-col bg-[hsl(222_47%_10%)] border-r border-[hsl(222_30%_18%)] p-4 text-slate-200">
        <div className="flex items-center gap-3 mb-6 px-2">
          <img 
            src="/logo-white.png" 
            alt="ResaWork" 
            className="h-9"
          />
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto -mx-1 px-1">
          {/* Items épinglés - mis en avant */}
          <div className="space-y-1">
            {pinnedItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                className={({ isActive }) => getNavItemClass(item, isActive)}
              >
                <item.icon className="w-[18px] h-[18px]" />
                <span className="text-sm flex-1">{item.label}</span>
                {item.pendingBadge > 0 && (
                  <span
                    className="ml-auto inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-orange-500 text-white min-w-[18px]"
                    data-testid={`nav-pending-desktop-${item.path.split('/').pop()}`}
                    title={`${item.pendingBadge} compte(s) en attente de validation`}
                  >
                    {item.pendingBadge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>

          {/* Séparateur visuel */}
          <div className="pt-5 mt-3 border-t border-[hsl(222_30%_18%)]">
            <p className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-[0.12em]">
              Configuration
            </p>
          </div>

          {/* Groupes déployables */}
          <div className="space-y-0.5">
            {collapsibleGroups.map((group) => {
              const isOpen = !!openGroups[group.key];
              const hasActiveChild = group.items.some(it => location.pathname.startsWith(it.path));
              return (
                <div key={group.key}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.key)}
                    data-testid={`group-toggle-${group.key}`}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg transition-colors ${
                      hasActiveChild
                        ? 'text-white font-medium'
                        : 'text-slate-300 hover:bg-white/[0.04] hover:text-white'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <group.icon className="w-[18px] h-[18px]" />
                      <span className="text-sm font-medium">{group.title}</span>
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="ml-4 pl-3 border-l border-[hsl(222_30%_22%)] space-y-0.5 mt-0.5 mb-1.5">
                      {group.items.map((item) => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          end={item.end}
                          data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                          className={({ isActive }) => getNavItemClass(item, isActive)}
                        >
                          <item.icon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        <div className="pt-4 border-t border-[hsl(222_30%_18%)]">
          <GuideTour autoStartForNewUsers={true} />
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-300 hover:text-red-300 hover:bg-red-500/10"
            data-testid="logout-btn"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
          <div className="mt-3 pt-3 border-t border-[hsl(222_30%_18%)] text-slate-500 text-xs">
            <FooterMinimal />
            <VersionBadge />
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto relative">
        {/* Top bar with Company Logo - Desktop Only */}
        <div className="hidden lg:flex items-center justify-end gap-4 p-4 border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-20">
          {/* Client approval mode indicator */}
          {companyInfo?.require_client_approval !== undefined && (
            <button
              type="button"
              onClick={() => navigate('/company/settings')}
              data-testid="client-approval-mode-indicator"
              title="Cliquez pour modifier ce réglage"
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                companyInfo.require_client_approval
                  ? 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
                  : 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
              }`}
            >
              {companyInfo.require_client_approval ? (
                <ClipboardList className="w-3.5 h-3.5" />
              ) : (
                <CheckCircle2 className="w-3.5 h-3.5" />
              )}
              <span>
                Validation clients : <strong>{companyInfo.require_client_approval ? 'Manuelle' : 'Automatique'}</strong>
              </span>
            </button>
          )}
          {/* Push Notifications Toggle */}
          <PushNotificationToggle />
          {/* Notifications */}
          <NotificationBell />
          <button 
            onClick={() => navigate('/company/settings')}
            className="flex items-center gap-3 px-4 py-2 rounded-xl hover:bg-muted/50 transition-colors border border-transparent hover:border-border"
            data-testid="company-account-btn"
          >
            {companyInfo.logo_url ? (
              <img 
                src={getCompanyLogoUrl(companyInfo.logo_url)} 
                alt={companyInfo.name} 
                className="w-10 h-10 rounded-full object-cover border" loading="lazy" decoding="async"
              />
            ) : (
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">{companyInfo.name?.charAt(0) || 'E'}</span>
              </div>
            )}
            <div className="text-left">
              <p className="text-sm font-medium">{companyInfo.name || 'Mon Entreprise'}</p>
              <p className="text-xs text-muted-foreground">Mon compte</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <div className="p-4 lg:p-8">
        <GlobalHealthBanner />
        <Routes>
          <Route index element={<DashboardOverview />} />
          <Route path="clients" element={<ClientsPage />} />
          <Route path="trash" element={<TrashPage />} />
          <Route path="clients/:clientId" element={<ClientDetailPageNew />} />
          <Route path="mrr" element={<MRRDashboardPage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="interventions" element={<CompletedInterventionsPage />} />
          <Route path="reviews" element={<ReviewsPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="collaborators" element={<CollaboratorsPage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="slot-types" element={<SlotTypesPage />} />
          <Route path="work-hours" element={<WorkHoursPage />} />
          <Route path="closures" element={<ClosuresPage />} />
          <Route path="leave-requests" element={<LeaveRequestsPage />} />
          <Route path="leave-history" element={<LeaveHistoryPage />} />
          <Route path="widgets" element={<WidgetsPage />} />
          <Route path="widgets/:widgetId" element={<WidgetEditor />} />
          {/* Page unifiée des demandes - une seule URL */}
          <Route path="requests" element={<UnifiedRequestsPage pageTitle="Demandes" pageSubtitle="Gérez toutes vos demandes en un seul endroit" />} />
          {/* Planning RDV commerciaux - ancien onglet conservé */}
          <Route path="rdv-planning" element={<RdvPlanningPage />} />
          {/* Anciennes routes redirigées vers la page unifiée */}
          <Route path="requests/appointments" element={<Navigate to="/company/requests" replace />} />
          <Route path="requests/video" element={<Navigate to="/company/requests" replace />} />
          <Route path="requests/bookings" element={<Navigate to="/company/requests" replace />} />
          <Route path="punctual" element={<Navigate to="/company/requests" replace />} />
          <Route path="appointments" element={<Navigate to="/company/requests" replace />} />
          <Route path="quote-requests" element={<Navigate to="/company/requests" replace />} />
          <Route path="subscriptions/:subscriptionId" element={<SubscriptionDetailPage />} />
          <Route path="planning" element={<ErrorBoundary featureName="le Planning"><AdvancedPlanningPage /></ErrorBoundary>} />
          <Route path="planning-v2" element={<ErrorBoundary featureName="le Planning v2"><UnifiedPlanningPage /></ErrorBoundary>} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="account" element={<Navigate to="/company/settings" replace />} />
          <Route path="subscription/success" element={<SubscriptionSuccessPage />} />
        </Routes>
        </div>
        {/* Floating quick action menu */}
        <QuickActionFab />
      </main>
      </div>
      {/* In-app new version notification */}
      <NewVersionToast />
    </div>
  );
}
